import React, { useState, useEffect, useCallback, useMemo } from 'react';
import JSZip from 'jszip';
import {
  LayoutDashboard, Users, Briefcase, DollarSign, Link, Mail, Calendar, BarChart3,
  BookOpen, Download, Award, Settings, LogOut, Search, Plus, X, Copy, Check,
  ChevronDown, Filter, TrendingUp, TrendingDown, Clock, Phone, MapPin, FileText,
  Upload, Send, Eye, Archive, Star, RefreshCw, AlertCircle, CheckCircle, XCircle,
  Plane, Landmark, GraduationCap, Video, File, HelpCircle, MessageCircle,
  Target, ArrowUpRight, ArrowDownRight, Wallet, CreditCard, PiggyBank, Globe,
  BadgeCheck, Layers, UserPlus, Share2, Bell, ChevronRight, ChevronLeft,
  MoreHorizontal, Shield, Info, SlidersHorizontal, Info as InfoIcon,
  LayoutGrid, List, Lock, EyeOff, Paperclip
} from 'lucide-react';
import { safeFetch } from '../lib/api';
import { withTeamSelection, noteTeamSelectionFailure } from '../lib/teamSelection';
import type { RoleSwitchControl } from '../lib/roleSwitch';
import { notifyToast } from '../lib/toastStore';
import { firstUsablePersonName } from '../lib/nameUtils';
import { auth, db, resolveFirebaseDocumentUrl } from '../lib/firebase';
import { brand as brandConfig } from '../lib/branding';
import { collection, query, orderBy, getDocs, setDoc, doc, addDoc, updateDoc, where } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import ReferrerCandidatesView from './referrer/ReferrerCandidatesView';
import ReferrerHomePanel from './referrer/ReferrerHomePanel';
import {
  STAGE_LABELS,
  STAGE_BADGE_CLASSES,
  resolveCandidateStage,
  candidateStageLabel,
} from '../lib/referrerStages';
import { summarizeCommissions, commissionAmount, commissionHistoryEntries } from '../lib/referrerCommissions';
import { REFERRER_RESTRICTED_NOTICE } from '../lib/orgApproval';
import { COUNTRIES } from '../lib/constants';
import ProgramCardGrid from './shared/ProgramCardGrid';
import PasswordSetForm from './shared/PasswordSetForm';
import { KpiCard } from './shared/DashboardPrimitives';
import ReferrerCalendarView from './referrer/ReferrerCalendarView';
import ReferrerPerformanceView from './referrer/ReferrerPerformanceView';
import ReferrerInboxView from './referrer/ReferrerInboxView';
import ReferrerSupportView from './referrer/ReferrerSupportView';
import OrgPortalSpecialRequests from './shared/OrgPortalSpecialRequests';
import ApplicantDetail from './admin/ApplicantDetail';
import OrgTeamManagement from './shared/OrgTeamManagement';
import TeamSwitcher from './shared/TeamSwitcher';
import Modal from './shared/Modal';
import MyEventsView from './shared/MyEventsView';
import DashboardShell from './shared/DashboardShell';
import UserMenu from './shared/UserMenu';
import { pageWindow } from '../lib/pageWindow';
import { getFrontendUrl } from '../lib/platformDomain';
import { usePath, navigate, parseRoute } from '../lib/router';

interface ReferrerDashboardProps {
  onLogout: () => void;
  userEmail: string;
  userId: string;
  userProfile: any;
  isApproved?: boolean;
  /**
   * PR-13b: restricted mode — the referrer org is DEACTIVATED. The
   * dashboard renders read-only (candidates + commission history stay
   * viewable) with the restriction banner; referring actions are
   * disabled here and refused server-side regardless.
   */
  restricted?: boolean;
  roleSwitch?: RoleSwitchControl;
}

// ── Mock data helpers ──────────────────────────────────────────────
const generateReferralCode = (uid: string) => {
  const hash = uid.slice(0, 6).toUpperCase();
  return `REF${hash}`;
};

// (The retired referrer-only stage vocabulary — 'Application Started',
// 'Documents Uploaded', … — was removed in PR-2 §2.2. Referrer surfaces now
// resolve the canonical 8 workflow stages via src/lib/referrerStages.)

const TRAINING_MODULES = [
  { id: 'j1-rules', title: 'J-1 Exchange Program Rules', duration: '45 min', completed: true },
  { id: 'program-updates', title: '2026 Program Updates', duration: '20 min', completed: false },
  { id: 'employer-guide', title: 'Employer Guidelines & Best Practices', duration: '35 min', completed: false },
  { id: 'visa-process', title: 'Visa Process Walkthrough', duration: '60 min', completed: true },
  { id: 'compliance', title: 'Compliance & Documentation', duration: '40 min', completed: false },
  { id: 'candidate-screening', title: 'Candidate Screening Techniques', duration: '30 min', completed: false },
];

const MARKETING_ASSETS = [
  { id: 'flyer-1', name: 'ISATS Program Flyer 2026', type: 'pdf', size: '2.4 MB' },
  { id: 'video-1', name: 'Welcome Video — J-1 Overview', type: 'video', size: '18 MB' },
  { id: 'poster-1', name: 'Camp Counselor Poster', type: 'image', size: '4.1 MB' },
  { id: 'social-1', name: 'Social Media Kit — Q1', type: 'zip', size: '12 MB' },
  { id: 'logo-pack', name: 'ISATS Logo Pack (PNG/SVG)', type: 'zip', size: '3.2 MB' },
  { id: 'brochure-1', name: 'Employer Brochure', type: 'pdf', size: '5.8 MB' },
];

// ── Dashboard component ─────────────────────────────────────────────

  const SimpleBarChart = ({ data, labels, color = 'bg-brand-primary' }: { data: number[]; labels: string[]; color?: string }) => {
    const max = Math.max(...data, 1);
    return (
      <div className="flex items-end space-x-2 h-40">
        {data.map((val, i) => (
          <div key={i} className="flex-1 flex flex-col items-center space-y-1">
            <div className="w-full relative" style={{ height: `${(val / max) * 100}%`, minHeight: '4px' }}>
              <div className={`absolute bottom-0 left-0 right-0 rounded-t-md ${color} opacity-80 hover:opacity-100 transition`} style={{ height: '100%' }} />
            </div>
            <span className="text-[10px] text-slate-500">{labels[i]}</span>
            <span className="text-[10px] font-semibold text-slate-700">{val}</span>
          </div>
        ))}
      </div>
    );
  };

  // ── Stage badge renderer — canonical workflow stage (§2.2) ─────────

  const StageBadge = ({ candidate }: { candidate: any }) => {
    const stageKey = resolveCandidateStage(candidate);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${STAGE_BADGE_CLASSES[stageKey]}`}>
        {STAGE_LABELS[stageKey]}
      </span>
    );
  };

/**
 * Commission detail modal (plan §2.4) — shared by the enhanced Commission
 * Tracking view and the read-only My Commissions view so a candidate's
 * stamped amount and history look identical everywhere.
 */
function CommissionDetailModal({ commission, onClose }: { commission: any; onClose: () => void }) {
  // REF-16: on the shared Modal (portal, modal-stack layer, scroll
  // lock, Escape) with a sticky header and footer — the hand-rolled
  // overlay scrolled its title, close button and Close action away
  // with the content.
  return (
    <Modal onClose={onClose} ariaLabel="Commission Record Details">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 pt-5 pb-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
              <DollarSign className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900">Commission Record Details</h3>
              <p className="text-[11px] text-slate-500">ID: {commission.id}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4">
        <div className="space-y-4 text-xs">
          {/* Candidate Summary */}
          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Candidate Information</p>
            <div className="flex justify-between items-center">
              <span className="font-bold text-slate-900 text-sm">{commission.candidateName}</span>
              <span className="text-slate-500">{commission.candidateEmail || '—'}</span>
            </div>
            <div className="flex justify-between text-slate-600 pt-1 border-t border-slate-200/60">
              <span>Program: <strong>{commission.program}</strong></span>
              <span>Application ID: <strong className="font-mono">{commission.applicationId}</strong></span>
            </div>
          </div>

          {/* Status Breakdown Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Workflow Stage</p>
              <p className="font-bold text-slate-900">{candidateStageLabel(commission)}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Commission Status</p>
              <span className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] ${commission.commissionStatus === 'committed' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                {commission.commissionStatus === 'committed' ? 'Committed' : 'Not Committed'}
              </span>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Payout Status</p>
              <span className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] ${
                commission.payoutStatus === 'cancelled' ? 'bg-rose-100 text-rose-800'
                  : commission.payoutProcessed || commission.payoutStatus === 'queued' ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {commission.payoutStatus === 'cancelled' ? 'Cancelled'
                  : commission.payoutProcessed || commission.payoutStatus === 'queued'
                    ? (commission.payoutProcessed ? 'Payout Processed' : 'Queued')
                  : 'Not Queued'}
              </span>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Candidate Profile</p>
              <span className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] ${
                ['archived', 'cancelled', 'ineligible', 'closed'].includes((commission.candidateStatus || '').toLowerCase())
                  ? 'bg-rose-100 text-rose-800'
                  : 'bg-emerald-100 text-emerald-800'
              }`}>
                {commission.candidateStatus || 'Active'}
              </span>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Invoice / Finance</p>
              <p className="font-bold text-slate-900">{commission.financeStatus || 'Unpaid'}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Commission (stamped at referral)</p>
              <p className="font-mono font-bold text-emerald-700 text-sm">${commissionAmount(commission).toLocaleString()}</p>
            </div>
          </div>

          {/* §2.4: per-candidate history — the referral stamp plus any admin
              adjustments, attributed and dated. */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2.5">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">History</p>
            {commissionHistoryEntries(commission).length === 0 ? (
              <p className="text-[11px] text-slate-500">No history recorded yet.</p>
            ) : (
              <ul className="space-y-2.5">
                {commissionHistoryEntries(commission).map((h, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800">{h.label}</p>
                      {h.detail && <p className="text-[11px] text-slate-500">{h.detail}</p>}
                    </div>
                    {h.at && <span className="text-[11px] text-slate-500 shrink-0">{new Date(h.at).toLocaleDateString()}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Attachment & Payment Details — replaces the old eligibility
              box. Referrers can review payout details and open any
              admin-uploaded commission / payment attachment. */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" /> Payment Details &amp; Attachments
              </p>
              {commission.payoutProcessed && (
                <span className="inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] bg-emerald-100 text-emerald-800">Payout Processed</span>
              )}
            </div>

            {commission.payment ? (
              <>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200/70">
                    <p className="text-[11px] font-bold text-slate-500 uppercase mb-0.5">Transaction ID</p>
                    <p className="font-mono font-bold text-slate-800 text-[11px] break-all">{commission.payment.transactionId || '—'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200/70">
                    <p className="text-[11px] font-bold text-slate-500 uppercase mb-0.5">Payout Method</p>
                    <p className="font-bold text-slate-800 text-[11px]">{commission.payment.payoutMethod || '—'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200/70">
                    <p className="text-[11px] font-bold text-slate-500 uppercase mb-0.5">Amount Paid</p>
                    <p className="font-mono font-bold text-emerald-700 text-sm">${(Number(commission.payment.amount || commission.amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200/70">
                    <p className="text-[11px] font-bold text-slate-500 uppercase mb-0.5">Payout Date</p>
                    <p className="font-bold text-slate-800 text-[11px]">
                      {commission.payment.payoutDate
                        ? new Date(commission.payment.payoutDate).toLocaleDateString()
                        : commission.payoutProcessedAt
                          ? new Date(commission.payoutProcessedAt).toLocaleDateString()
                          : '—'}
                    </p>
                  </div>
                </div>
                {commission.payment.note && (
                  <p className="text-xs text-slate-600 bg-white rounded-lg p-2.5 border border-slate-200/70"><strong className="text-slate-500">Note:</strong> {commission.payment.note}</p>
                )}
                {commission.payment.attachment && (
                  <div className="bg-white rounded-lg p-3 border border-slate-200/70 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 shrink-0"><FileText className="h-4 w-4" /></div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">{commission.payment.attachment.fileName || 'Payment document'}</p>
                        <p className="text-[11px] text-slate-500">
                          {(commission.payment.attachment.mimeType || 'document').toUpperCase()}
                          {commission.payment.attachment.fileSize ? ` · ${(Number(commission.payment.attachment.fileSize) / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    <a href={commission.payment.attachment.fileUrl} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg transition shrink-0">
                      <Download className="h-3.5 w-3.5" /> View
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg p-3 border border-dashed border-slate-200 text-center">
                <Paperclip className="h-4 w-4 mx-auto text-slate-300 mb-1" />
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  {commission.payoutProcessed || commission.payoutStatus === 'queued' || commission.status === 'Queued'
                    ? 'No attachment uploaded with this payout yet. Payment documents will appear here once they are available.'
                    : 'No payment attachments yet. Commission payment documents and payout details will appear here once your payout has been processed.'}
                </p>
              </div>
            )}
          </div>
        </div>
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-slate-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

// REF-05/REF-19: one search + status filter for both commission views
// (enhanced Commission Tracking and non-enhanced My Commissions), so the
// two presentations can never drift apart. Search matches candidate
// name, program and candidate email; the status buckets and their
// membership are the enhanced view's, unchanged.
function filterCommissionRows(rows: any[], search: string, statusFilter: string): any[] {
  return rows.filter((c: any) => {
    if (search) {
      const q = search.toLowerCase();
      const name = (c.candidateName || '').toLowerCase();
      const prog = (c.program || '').toLowerCase();
      const email = (c.candidateEmail || '').toLowerCase();
      if (!name.includes(q) && !prog.includes(q) && !email.includes(q)) return false;
    }
    if (statusFilter !== 'All') {
      const st = String(c.status || c.eligibility || '').toLowerCase();
      const paidOut = !!(c.payoutProcessed || c.status === 'Paid');
      if (statusFilter === 'Committed' && !['approved', 'eligible', 'issued', 'committed'].includes(st)) return false;
      if (statusFilter === 'Queued' && !(st === 'queued' && !paidOut)) return false;
      if (statusFilter === 'Paid' && !paidOut) return false;
      if (statusFilter === 'Pending' && !['not committed', 'pending', 'under review', 'on hold'].includes(st)) return false;
      if (statusFilter === 'Ineligible' && !['cancelled', 'ineligible', 'rejected'].includes(st)) return false;
    }
    return true;
  });
}

// REF-05: chip colour classes shared by the commissions table (md+)
// and the mobile cards (< md), so both presentations render identical
// status colours for the same row. Labels stay at the call sites
// because the two presentations size chip text differently.
function placementChipClass(c: any): string {
  return ['hired / placed', 'hired', 'placed', 'confirmed'].includes((c.placementStatus || '').toLowerCase())
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    : (c.placementStatus || '').toLowerCase() === 'matched'
    ? 'bg-purple-50 text-purple-700 border border-purple-200'
    : 'bg-slate-100 text-slate-600 border border-slate-200';
}

function financeChipClass(c: any): string {
  return c.financeStatusSummary === 'All Invoice(s) Cleared' || c.financeStatus === 'Paid'
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    : c.financeStatusSummary === 'Pending Invoice(s)'
    ? 'bg-amber-50 text-amber-700 border border-amber-200'
    : 'bg-slate-100 text-slate-600 border border-slate-200';
}

function commissionStatusChipClass(c: any): string {
  const st = String(c.status || c.eligibility || 'Pending').toLowerCase();
  const paidOut = !!(c.payoutProcessed || c.status === 'Paid');
  return paidOut
    ? 'bg-emerald-100 text-emerald-800'
    : st === 'queued'
    ? 'bg-purple-100 text-purple-800'
    : st === 'committed' || st === 'approved' || st === 'eligible' || st === 'issued'
    ? 'bg-blue-100 text-blue-800'
    : st === 'cancelled' || st === 'ineligible' || st === 'rejected'
    ? 'bg-rose-100 text-rose-800'
    : st === 'not committed' || st === 'pending' || st === 'under review' || st === 'on hold'
    ? 'bg-orange-100 text-orange-800'
    : 'bg-amber-100 text-amber-800';
}

export default function ReferrerDashboard({ onLogout, userEmail, userId, userProfile, isApproved, restricted, roleSwitch }: ReferrerDashboardProps) {
  const [view, setView] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const p = window.location.pathname;
      const r = parseRoute(p);
      if (r.type === 'applications_detail') return 'candidates';
      if (r.type === 'events_detail') return 'my-events';
      if (r.type === 'base') {
        const norm = r.section.toLowerCase();
        if (norm === 'applications' || norm === 'applicants' || norm === 'candidates') return 'candidates';
        if (norm === 'events' || norm === 'my-events') return 'my-events';
        return norm;
      }
    }
    return 'dashboard';
  });
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('isats_banner_dismissed') === 'true';
    }
    return false;
  });
  const [loading, setLoading] = useState(true);
  const [enhancedPermission, setEnhancedPermission] = useState(false);

  // FIX 2026-10-07: sync view with URL changes (browser back/forward or
  // programmatic navigate() calls that don't go through setView).
  React.useEffect(() => {
    const syncViewFromUrl = () => {
      if (typeof window === 'undefined') return;
      const p = window.location.pathname;
      // Only sync for referrer dashboard routes
      if (!p.startsWith('/dashboard') && p !== '/') return;
      const r = parseRoute(p);
      let newView = 'dashboard';
      if (r.type === 'applications_detail') newView = 'candidates';
      else if (r.type === 'events_detail') newView = 'my-events';
      else if (r.type === 'base') {
        const norm = r.section.toLowerCase();
        if (norm === 'applications' || norm === 'applicants' || norm === 'candidates') newView = 'candidates';
        else if (norm === 'events' || norm === 'my-events') newView = 'my-events';
        else newView = norm;
      }
      setView((prev) => (prev !== newView ? newView : prev));
    };
    window.addEventListener('popstate', syncViewFromUrl);
    window.addEventListener('app:navigate', syncViewFromUrl);
    return () => {
      window.removeEventListener('popstate', syncViewFromUrl);
      window.removeEventListener('app:navigate', syncViewFromUrl);
    };
  }, []);
  const [isSuperAgent, setIsSuperAgent] = useState(false);
  const [referrerProfile, setReferrerProfile] = useState<any>(null);
  const [enhancedViewApp, setEnhancedViewApp] = useState<any>(null);
  const [enhancedDetailTab, setEnhancedDetailTab] = useState<string>('profile');
  const [enhancedDocs, setEnhancedDocs] = useState<any[]>([]);
  const [enhancedComments, setEnhancedComments] = useState<any[]>([]);

  const [slideInCandidate, setSlideInCandidate] = useState<any>(null);
  const [slideInLoading, setSlideInLoading] = useState(false);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Edit states for Advanced Referrers (Host Employer & Itinerary Sections)
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [editAssignmentEmployer, setEditAssignmentEmployer] = useState('');
  const [editAssignmentRole, setEditAssignmentRole] = useState('');
  const [editAssignmentCity, setEditAssignmentCity] = useState('');
  const [editAssignmentState, setEditAssignmentState] = useState('');
  const [editAssignmentCountry, setEditAssignmentCountry] = useState('United States');

  const [isEditingVisa, setIsEditingVisa] = useState(false);
  const [editVisaNo, setEditVisaNo] = useState('');
  const [editVisaStatus, setEditVisaStatus] = useState('Pending');
  const [editVisaDate, setEditVisaDate] = useState('');
  const [editVisaLocation, setEditVisaLocation] = useState('');

  const [isEditingFlight, setIsEditingFlight] = useState(false);
  const [editFlightAirline, setEditFlightAirline] = useState('');
  const [editFlightNo, setEditFlightNo] = useState('');
  const [editFlightDepDateTime, setEditFlightDepDateTime] = useState('');
  const [editFlightArrDateTime, setEditFlightArrDateTime] = useState('');
  const [editFlightDepAirport, setEditFlightDepAirport] = useState('');
  const [editFlightArrAirport, setEditFlightArrAirport] = useState('');
  const [editFlightNotes, setEditFlightNotes] = useState('');

  const [isEditingEmbassy, setIsEditingEmbassy] = useState(false);
  const [editEmbassyDate, setEditEmbassyDate] = useState('');
  const [editEmbassyLocation, setEditEmbassyLocation] = useState('');
  const [editEmbassyResult, setEditEmbassyResult] = useState('Pending');

  const [isEditingDs160, setIsEditingDs160] = useState(false);
  const [editDs160No, setEditDs160No] = useState('');
  const [editDs160QuestionAnswer, setEditDs160QuestionAnswer] = useState('');
  const [editDs160Status, setEditDs160Status] = useState('Pending');

  // Reactive comments states for referrers
  const [referrerNewComment, setReferrerNewComment] = useState('');
  const [referrerReplyInputs, setReferrerReplyInputs] = useState<Record<string, string>>({});
  const [referrerOpenReplyId, setReferrerOpenReplyId] = useState<string | null>(null);
  const [referrerEditingCommentId, setReferrerEditingCommentId] = useState<string | null>(null);
  const [referrerEditingCommentText, setReferrerEditingCommentText] = useState('');
  const [referrerEditingReplyId, setReferrerEditingReplyId] = useState<string | null>(null);
  const [referrerEditingReplyText, setReferrerEditingReplyText] = useState('');

  // Material selection states
  const [selectedTrainingProgramId, setSelectedTrainingProgramId] = useState<string | null>(null);
  const [selectedMarketingProgramId, setSelectedMarketingProgramId] = useState<string | null>(null);

  const [previewDoc, setPreviewDoc] = useState<any | null>(null);

  // Passport & Immigration edit states
  const [isEditingPassport, setIsEditingPassport] = useState(false);
  const [editPassportNo, setEditPassportNo] = useState('');
  const [editPassportCountry, setEditPassportCountry] = useState('');
  const [editPassportIssue, setEditPassportIssue] = useState('');
  const [editPassportExpiry, setEditPassportExpiry] = useState('');
  const [editPassVisaStatus, setEditPassVisaStatus] = useState('');
  const [editDs2019, setEditDs2019] = useState('');
  const [editSevisId, setEditSevisId] = useState('');
  const [editIntendedArrival, setEditIntendedArrival] = useState('');
  const [editUsDestination, setEditUsDestination] = useState('');

  // Program preferences edit states
  const [isEditingProgram, setIsEditingProgram] = useState(false);
  const [editProgram, setEditProgram] = useState('');
  const [editProgramTerm, setEditProgramTerm] = useState('');
  const [editProgStart, setEditProgStart] = useState('');
  const [editProgDuration, setEditProgDuration] = useState('');
  const [editProgState, setEditProgState] = useState('');
  const [editProgIndustry, setEditProgIndustry] = useState('');
  const [editProgEnglish, setEditProgEnglish] = useState('');
  const [editProgSkills, setEditProgSkills] = useState('');
  const [editProgRelocate, setEditProgRelocate] = useState('');
  const [editProgNotes, setEditProgNotes] = useState('');

  // Overview stats
  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeCandidates: 0,
    interviewing: 0,
    placed: 0,
    rejected: 0,
    offersAccepted: 0,
    visaProcess: 0,
    traveling: 0,
    programStarts: 0,
    totalEarnings: 0,
    pendingCommissions: 0,
    queuedCommissions: 0,
    paidCommissions: 0,
  });

  // Jobs
  const [jobs, setJobs] = useState<any[]>([]);
  // Program-card drill-down for the Job Board: null = program cards landing.
  const [jobsProgram, setJobsProgram] = useState<string | null>(null);
  const [jobFilterPrograms, setJobFilterPrograms] = useState<string[]>([]);

  // Program cards with live job counts (server programs, never hardcoded).
  const jobProgramCards = useMemo(() => {
    const counts = new Map<string, number>();
    jobs.forEach((j: any) => {
      const prog = j.program || j.programType || (j.programs && j.programs[0]) || '';
      if (prog) counts.set(prog, (counts.get(prog) || 0) + 1);
    });
    const names = new Set<string>([...jobFilterPrograms, ...counts.keys()]);
    return Array.from(names)
      .map(name => ({ name, jobCount: counts.get(name) || 0 }))
      .sort((a, b) => b.jobCount - a.jobCount || a.name.localeCompare(b.name));
  }, [jobs, jobFilterPrograms]);
  const [savedJobIds, setSavedJobIds] = useState<string[]>([]);
  const [jobSearch, setJobSearch] = useState('');
  const [jobPage, setJobPage] = useState(1);
  const JOBS_PER_PAGE = 12;
  const [jobViewMode, setJobViewMode] = useState<'card' | 'list'>('card');

  // Referred job applications and popup states
  const [referrerJobApplications, setReferrerJobApplications] = useState<any[]>([]);
  const [selectedJobDetails, setSelectedJobDetails] = useState<any | null>(null);
  const [referCandidateIds, setReferCandidateIds] = useState<string[]>([]);
  const [referringLoader, setReferringLoader] = useState(false);

  // Candidates
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [candidateForm, setCandidateForm] = useState({ firstName: '', lastName: '', email: '', phone: '', country: '', notes: '', programType: '', programName: '', programTerm: '', sendInvite: false });
  const [candidatePage, setCandidatePage] = useState(1);
  const [referrerPrograms, setReferrerPrograms] = useState<any[]>([]);
  // Programs load state: the "Add New Candidate" combobox must never be
  // permanently stuck on "Loading programs...". A failed fetch surfaces a
  // retry affordance instead of a dead loading state.
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programsError, setProgramsError] = useState(false);
  // Programs are loaded independently with retry: the "Add New Candidate"
  // combobox must never be permanently stuck on "Loading programs...".
  const loadPrograms = async (attempt = 1): Promise<void> => {
    setProgramsLoading(true);
    setProgramsError(false);
    try {
      const data = (await safeFetch('/api/referrer/programs')) as { programs?: any[] };
      setReferrerPrograms(data?.programs || []);
      setProgramsLoading(false);
    } catch {
      if (attempt < 3) {
        // Exponential backoff retry (1s, 2s) before surfacing the error.
        await new Promise(r => setTimeout(r, attempt * 1000));
        return loadPrograms(attempt + 1);
      }
      setProgramsLoading(false);
      setProgramsError(true);
    }
  };
  const [addingCandidate, setAddingCandidate] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailCheckMsg, setEmailCheckMsg] = useState('');
  // §2.3: after a successful add, the modal swaps to a success panel showing
  // the created applicant ID + what happens next (invite sent or not).
  const [addSuccess, setAddSuccess] = useState<{ candidate: any; sendInvite: boolean } | null>(null);

  // Commissions
  const [commissions, setCommissions] = useState<any[]>([]);
  const [commSearch, setCommSearch] = useState('');
  const [commStatusFilter, setCommStatusFilter] = useState('All');
  const [commPage, setCommPage] = useState(1);
  // REF-19: My Commissions (non-enhanced) carries its own search,
  // status filter and page state, mirroring the enhanced view's
  // affordances without sharing its state.
  const [myCommSearch, setMyCommSearch] = useState('');
  const [myCommStatusFilter, setMyCommStatusFilter] = useState('All');
  const [myCommPage, setMyCommPage] = useState(1);
  const COMM_PER_PAGE = 10;
  const [selectedCommission, setSelectedCommission] = useState<any | null>(null);

  // Referral links
  // FIX 2026-10-07: guard against double-scheme (https://https://) if the
  // configured frontend URL already includes a scheme. Strip all leading
  // schemes and re-add exactly one.
  const [referralCode] = useState(() => generateReferralCode(userId));
  const frontendUrl = (() => {
    const raw = getFrontendUrl().replace(/^(https?:\/\/)+/i, '');
    return `https://${raw}`;
  })();
  const [referralLink] = useState(() => `${frontendUrl}/invite?ref=${generateReferralCode(userId)}`);
  // §5.2: ONE invite code everywhere. The server-derived code on the
  // profile (`inviteCode`, attached by GET /api/referrer/profile — the
  // same value admin surfaces show) wins; a stored `referralCode` is next;
  // the locally generated code is only a pre-profile-load placeholder.
  // Every surface below renders/copies these two values, never the raw
  // state, so the header link, the Referral Links page, the home panel,
  // and copied links can never diverge.
  const effectiveReferralCode = referrerProfile?.inviteCode || referrerProfile?.referralCode || referralCode;
  const effectiveReferralLink = `${frontendUrl}/invite?ref=${effectiveReferralCode}`;
  const [copiedLink, setCopiedLink] = useState(false);

  // Invitations
  const [invitations, setInvitations] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Calendar
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);

  // Performance
  const [performanceData, setPerformanceData] = useState<any>(null);

  // (Leaderboard removed)

  // Change Password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');

  // Profile / settings — seeded from the signed-in user, then synced from
  // the referrer ORG record when it loads (§2.5: org fields must populate
  // from the org record, not sit blank).
  const [profileForm, setProfileForm] = useState({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    email: userEmail,
    phone: userProfile?.phoneNumber || '',
    company: userProfile?.companyName || '',
    taxId: userProfile?.taxId || '',
    bankAccount: userProfile?.bankAccount || '',
    bankRouting: userProfile?.bankRouting || '',
    country: '',
    address: '',
    website: '',
  });

  useEffect(() => {
    if (!referrerProfile) return;
    const r = referrerProfile;
    const contactParts = String(r.contactName || '').trim().split(/\s+/).filter(Boolean);
    setProfileForm(prev => ({
      ...prev,
      firstName: prev.firstName || contactParts[0] || '',
      lastName: prev.lastName || contactParts.slice(1).join(' ') || '',
      phone: prev.phone || r.contactPhone || r.phone || '',
      company: r.name || prev.company,
      taxId: r.taxId || prev.taxId,
      bankAccount: r.bankAccount || prev.bankAccount,
      bankRouting: r.bankRouting || prev.bankRouting,
      country: r.country || prev.country,
      address: r.address || prev.address,
      website: r.website || prev.website,
    }));
  }, [referrerProfile]);

  // Views this dashboard actually renders. Global nav:change events carry
  // tabs like 'applicants' / 'application' that belong to the admin routing
  // layer (App.tsx) — applying them here would blank out the candidates list
  // or the enhanced applicant detail right after it opens, so only accept
  // referrer views.
  const REFERRER_VIEWS = useMemo(() => new Set([
    'dashboard', 'candidates', 'enhanced-view', 'inbox', 'jobs', 'commissions',
    'referral-links', 'invitations', 'calendar', 'my-events', 'performance',
    'training', 'marketing', 'profile', 'agents', 'support', 'special-requests',
  ]), []);

  // Views reserved for Enhanced Referrers. Kept in sync with the nav:change
  // gate below; the render layer also checks this via `activeView`.
  // NOTE: 'commissions' is intentionally NOT here — every referrer sees
  // their own commissions read-only ("My Commissions" for non-enhanced).
  // Enhanced-only remains: team, performance analytics, events, training.
  const ENHANCED_ONLY_VIEWS = useMemo(() => new Set([
    'agents', 'training', 'marketing', 'calendar', 'my-events', 'performance', 'support',
  ]), []);

  // Views only the Super Agent may open. REF-02: 'agents' and
  // 'commissions' were removed from this set — both destinations are
  // advertised to every enhanced referrer (sidebar, mobile More sheet,
  // the home Payouts card), and each now presents what a non-super
  // member is entitled to instead of bouncing silently to Dashboard:
  // OrgTeamManagement answers members with its restricted notice, and
  // commissions renders MemberCommissionsView. The server stays
  // authoritative — /api/referrer/agents and /api/referrer/commissions
  // refuse non-super callers, so no team data reaches a member's
  // client. 'performance' stays gated: whether members get read-only
  // Performance is an open owner decision.
  const SUPER_ONLY_VIEWS = useMemo(() => new Set(['performance']), []);

  // Listen for nav changes from CollapsibleSidebar / BottomNav
  useEffect(() => {
    const handler = (e: any) => {
      const tab = e.detail?.tab;
      if (tab && REFERRER_VIEWS.has(tab)) {
        const enhancedOnly = ENHANCED_ONLY_VIEWS.has(tab) && !enhancedPermission;
        const superOnly = SUPER_ONLY_VIEWS.has(tab) && !isSuperAgent;
        if (enhancedOnly || superOnly) {
          setView('dashboard');
        } else {
          setView(tab);
        }
      }
    };
    window.addEventListener('nav:change', handler);
    return () => window.removeEventListener('nav:change', handler);
  }, [REFERRER_VIEWS, ENHANCED_ONLY_VIEWS, SUPER_ONLY_VIEWS, enhancedPermission, isSuperAgent]);

  // Lock body scroll for the Candidates slide-in drawer (prevents
  // jumping glitch). Dialogs lock through the shared Modal itself
  // since the REF-16 migration — Add Candidate included.
  useBodyScrollLock(!!slideInCandidate);

  // Load data on mount and when candidatePage changes
  useEffect(() => {
    loadDashboardData();
    // Programs load independently with retry so the candidate dialog's
    // combobox never gets permanently stuck on "Loading programs...".
    loadPrograms();
  }, [candidatePage]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Execute initial queries in parallel for high speed dashboard initialization
      const [
        profileRes,
        jobsRes,
        jobFiltRes,
        progRes,
        candRes,
        commRes,
        invRes,
        evRes,
        jaRes,
        perfRes
      ] = await Promise.allSettled([
        safeFetch('/api/referrer/profile'),
        safeFetch('/api/jobs'),
        safeFetch('/api/jobs/filters'),
        safeFetch('/api/referrer/programs'),
        safeFetch('/api/referrer/candidates'),
        safeFetch('/api/referrer/commissions'),
        safeFetch('/api/referrer/invitations'),
        safeFetch('/api/referrer/events'),
        safeFetch('/api/referrer/job-applications'),
        safeFetch('/api/referrer/performance')
      ]);

      if (profileRes.status === 'fulfilled' && profileRes.value?.referrer) {
        const rProfile = profileRes.value.referrer;
        setReferrerProfile(rProfile);
        const hasEnhanced = rProfile.enhancedPermission === true;
        setEnhancedPermission(hasEnhanced);
        const uid = auth.currentUser?.uid;
        const promotedCoSuper = (rProfile.linkedUsers || []).some((u: any) => u.uid === uid && u.agentRole === 'super');
        setIsSuperAgent(rProfile.userId === uid || promotedCoSuper);

      }

      if (jobsRes.status === 'fulfilled') setJobs(jobsRes.value?.jobs || []);
      // Programs load independently with retry (see loadPrograms).
      if (jobFiltRes.status === 'fulfilled' && Array.isArray(jobFiltRes.value?.programs)) {
        setJobFilterPrograms(jobFiltRes.value.programs);
      }
      if (progRes.status === 'fulfilled') setReferrerPrograms(progRes.value?.programs || []);

      let realCandidates: any[] = [];
      if (candRes.status === 'fulfilled' && candRes.value) {
        realCandidates = candRes.value.candidates || [];
      }
      setCandidates(realCandidates);

      // Compute stats from real candidates
      const active = realCandidates.filter((c: any) => c.status !== 'archived' && c.isArchived !== true);
      const masterStages = realCandidates.map((c: any) => typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0'));
      setStats({
        totalReferrals: realCandidates.length,
        activeCandidates: active.length,
        interviewing: masterStages.filter(s => s >= 2 && s <= 3).length,
        placed: masterStages.filter(s => s >= 4).length,
        rejected: realCandidates.filter((c: any) => c.status === 'rejected' || c.submissionStatus === 'rejected').length,
        offersAccepted: masterStages.filter(s => s >= 5).length,
        visaProcess: masterStages.filter(s => s >= 6 && s <= 7).length,
        traveling: masterStages.filter(s => s >= 8).length,
        programStarts: masterStages.filter(s => s >= 9).length,
        totalEarnings: 0,
        pendingCommissions: 0,
        queuedCommissions: 0,
        paidCommissions: 0,
      });

      if (commRes.status === 'fulfilled' && commRes.value) {
        const comms = commRes.value.commissions || [];
        setCommissions(comms);

        // §2.4: one totals source (lib/referrerCommissions) so the dashboard
        // card, the home payouts card, and My Commissions always agree.
        // Amounts are the per-candidate stamped amounts — never recomputed
        // from current program rates. `queuedCommissions` is the shell
        // card's "Payable" figure: under the commission lifecycle (PR-0b)
        // only QUEUED payouts have actually become payable (committed +
        // balance clear + admin-queued); the lib's payable bucket is
        // queued-only, so earlier-stage amounts stay pending.
        const summary = summarizeCommissions(comms);
        setStats(prev => ({
          ...prev,
          totalEarnings: summary.payable + summary.paid,
          pendingCommissions: summary.accruing + summary.payable,
          queuedCommissions: summary.payable,
          paidCommissions: summary.paid,
        }));
      }

      if (invRes.status === 'fulfilled') setInvitations(invRes.value?.invitations || []);
      if (evRes.status === 'fulfilled') {
        const rawEvs = evRes.value?.events || [];
        setCalendarEvents(rawEvs.filter((e: any) => !e.archived && e.status !== 'Archived'));
      }
      if (jaRes.status === 'fulfilled') setReferrerJobApplications(jaRes.value?.applications || []);
      if (perfRes.status === 'fulfilled') setPerformanceData(perfRes.value);

    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnhancedDocs = async (appId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(withTeamSelection(`/api/referrer/applications/${appId}/documents`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setEnhancedDocs(data.docs || []);
      }
    } catch { /* ignore */ }
  };

  const handleSaveAssignment = async () => {
    if (!enhancedViewApp) return;
    const isPlacementMutable = enhancedViewApp.placementStatus !== 'Confirmed' && enhancedViewApp.placementStatus !== 'Approved';
    if (!isPlacementMutable) {
      notifyToast('warning', "Placement is locked (Confirmed or Approved) and cannot be modified.");
      return;
    }
    try {
      await auth.currentUser?.getIdToken(true);
      const updatedAssignment = {
        employerName: editAssignmentEmployer,
        roleTitle: editAssignmentRole,
        city: editAssignmentCity,
        state: editAssignmentState,
        country: editAssignmentCountry,
      };
      const docRef = doc(db, 'applications', enhancedViewApp.applicationId);
      await updateDoc(docRef, { assignment: updatedAssignment, updatedAt: new Date().toISOString() });
      const updatedApp = { ...enhancedViewApp, assignment: updatedAssignment, updatedAt: new Date().toISOString() };
      setEnhancedViewApp(updatedApp);
      setCandidates(prev => prev.map(a => a.applicationId === enhancedViewApp.applicationId ? updatedApp : a));
      setIsEditingAssignment(false);
      notifyToast('success', "Placement details stored successfully.");
    } catch (err: any) {
      console.error("Save assignment failed:", err);
      notifyToast('error', "Failed storing placement details: " + err.message);
    }
  };

  const handleSaveVisaDetails = async () => {
    if (!enhancedViewApp) return;
    try {
      await auth.currentUser?.getIdToken(true);
      const updatedVisa = {
        visaNumber: editVisaNo,
        visaApplicationNumber: editVisaNo,
        visaType: 'J-1',
        visaApprovalStatus: editVisaStatus,
        embassyAppointmentDate: editVisaDate,
        embassyLocation: editVisaLocation,
      };
      const docRef = doc(db, 'applications', enhancedViewApp.applicationId);
      await updateDoc(docRef, { visaDetails: updatedVisa, updatedAt: new Date().toISOString() });
      const updatedApp = { ...enhancedViewApp, visaDetails: updatedVisa, updatedAt: new Date().toISOString() };
      setEnhancedViewApp(updatedApp);
      setCandidates(prev => prev.map(a => a.applicationId === enhancedViewApp.applicationId ? updatedApp : a));
      setIsEditingVisa(false);
      notifyToast('success', "Visa specifications stored successfully.");
    } catch (err: any) {
      console.error("Save visa failed:", err);
      notifyToast('error', "Failed storing visa specifications: " + err.message);
    }
  };

  const handleSaveFlightDetails = async () => {
    if (!enhancedViewApp) return;
    try {
      await auth.currentUser?.getIdToken(true);
      const updatedFlight = {
        airline: editFlightAirline,
        flightNumber: editFlightNo,
        departureDateTime: editFlightDepDateTime,
        arrivalDateTime: editFlightArrDateTime,
        departureAirport: editFlightDepAirport,
        arrivalAirport: editFlightArrAirport,
        additionalNotes: editFlightNotes,
      };
      const docRef = doc(db, 'applications', enhancedViewApp.applicationId);
      await updateDoc(docRef, { flightDetails: updatedFlight, updatedAt: new Date().toISOString() });
      const updatedApp = { ...enhancedViewApp, flightDetails: updatedFlight, updatedAt: new Date().toISOString() };
      setEnhancedViewApp(updatedApp);
      setCandidates(prev => prev.map(a => a.applicationId === enhancedViewApp.applicationId ? updatedApp : a));
      setIsEditingFlight(false);
      notifyToast('success', "Flight specifications stored successfully.");
    } catch (err: any) {
      console.error("Save flight failed:", err);
      notifyToast('error', "Failed storing flight specifications: " + err.message);
    }
  };

  const handleSaveEmbassyDetails = async () => {
    if (!enhancedViewApp) return;
    try {
      await auth.currentUser?.getIdToken(true);
      const updatedEmbassy = {
        embassyAppointmentDate: editEmbassyDate,
        embassyLocation: editEmbassyLocation,
        interviewResult: editEmbassyResult,
      };
      const docRef = doc(db, 'applications', enhancedViewApp.applicationId);
      await updateDoc(docRef, { embassyDetails: updatedEmbassy, updatedAt: new Date().toISOString() });
      const updatedApp = { ...enhancedViewApp, embassyDetails: updatedEmbassy, updatedAt: new Date().toISOString() };
      setEnhancedViewApp(updatedApp);
      setCandidates(prev => prev.map(a => a.applicationId === enhancedViewApp.applicationId ? updatedApp : a));
      setIsEditingEmbassy(false);
      notifyToast('success', "Embassy appointment specifications stored successfully.");
    } catch (err: any) {
      console.error("Save embassy failed:", err);
      notifyToast('error', "Failed storing embassy appointment specifications: " + err.message);
    }
  };

  const handleSaveDs160Details = async () => {
    if (!enhancedViewApp) return;
    try {
      await auth.currentUser?.getIdToken(true);
      const updatedDs160 = {
        ds160Number: editDs160No,
        ds160ConfirmationNumber: editDs160No,
        // Wave 4 (D-05): DS-160 security Q&A no longer collected; this
        // object REPLACES ds160Details, so saving also clears any legacy
        // stored answer on records a referrer edits.
        ds160SubmissionStatus: editDs160Status,
      };
      const docRef = doc(db, 'applications', enhancedViewApp.applicationId);
      await updateDoc(docRef, { ds160Details: updatedDs160, updatedAt: new Date().toISOString() });
      const updatedApp = { ...enhancedViewApp, ds160Details: updatedDs160, updatedAt: new Date().toISOString() };
      setEnhancedViewApp(updatedApp);
      setCandidates(prev => prev.map(a => a.applicationId === enhancedViewApp.applicationId ? updatedApp : a));
      setIsEditingDs160(false);
      notifyToast('success', "DS-160 Information stored successfully.");
    } catch (err: any) {
      console.error("Save DS-160 failed:", err);
      notifyToast('error', "Failed storing DS-160 Information: " + err.message);
    }
  };

  const handleSavePassport = async () => {
    if (!enhancedViewApp?.applicationId) return;
    try {
      const passportInfo = {
        passportNumber: editPassportNo,
        countryOfIssue: editPassportCountry,
        issueDate: editPassportIssue,
        expirationDate: editPassportExpiry,
        visaStatus: editPassVisaStatus,
        ds2019Number: editDs2019,
        sevisId: editSevisId,
        intendedArrivalDate: editIntendedArrival,
        usDestinationAddress: editUsDestination,
      };
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passportInfo, passport: passportInfo, immigration: passportInfo }),
      });
      if (res.success && res.candidate) {
        setEnhancedViewApp(res.candidate);
        setCandidates(prev => prev.map(c => c.id === enhancedViewApp.id || c.applicationId === enhancedViewApp.applicationId ? { ...c, ...res.candidate } : c));
        setIsEditingPassport(false);
        notifyToast('success', 'Passport details updated successfully');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save passport details');
    }
  };

  const handleSaveProgram = async () => {
    if (!enhancedViewApp?.applicationId) return;
    try {
      const programPreferences = {
        startDate: editProgStart,
        duration: editProgDuration,
        preferredState: editProgState,
        industry: editProgIndustry,
        englishLevel: editProgEnglish,
        skills: editProgSkills,
        relocationPreference: editProgRelocate,
        notes: editProgNotes,
      };
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          programId: editProgram,
          programType: editProgram,
          programTerm: editProgramTerm,
          programPreferences,
        }),
      });
      if (res.success && res.candidate) {
        setEnhancedViewApp(res.candidate);
        setCandidates(prev => prev.map(c => c.id === enhancedViewApp.id || c.applicationId === enhancedViewApp.applicationId ? { ...c, ...res.candidate } : c));
        setIsEditingProgram(false);
        notifyToast('success', 'Program details updated successfully');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save program details');
    }
  };

  const handleSavePersonal = async (updatedDetails: any) => {
    if (!enhancedViewApp?.applicationId) return;
    try {
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: updatedDetails.firstName || enhancedViewApp.firstName,
          lastName: updatedDetails.lastName || enhancedViewApp.lastName,
          personalDetails: updatedDetails,
        }),
      });
      if (res.success && res.candidate) {
        setEnhancedViewApp(res.candidate);
        setCandidates(prev => prev.map(c => c.id === enhancedViewApp.id || c.applicationId === enhancedViewApp.applicationId ? { ...c, ...res.candidate } : c));
        notifyToast('success', 'Personal details updated successfully');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save personal details');
    }
  };

  const handleSaveEducation = async (education: any) => {
    if (!enhancedViewApp?.applicationId) return;
    try {
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ education, educationHistory: education }),
      });
      if (res.success && res.candidate) {
        setEnhancedViewApp(res.candidate);
        setCandidates(prev => prev.map(c => c.id === enhancedViewApp.id || c.applicationId === enhancedViewApp.applicationId ? { ...c, ...res.candidate } : c));
        notifyToast('success', 'Education details updated successfully');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save education details');
    }
  };

  const handleSaveWork = async (workExperience: any) => {
    if (!enhancedViewApp?.applicationId) return;
    try {
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workExperience }),
      });
      if (res.success && res.candidate) {
        setEnhancedViewApp(res.candidate);
        setCandidates(prev => prev.map(c => c.id === enhancedViewApp.id || c.applicationId === enhancedViewApp.applicationId ? { ...c, ...res.candidate } : c));
        notifyToast('success', 'Work experience updated successfully');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save work experience');
    }
  };

  const handleSaveEmergency = async (emergencyContact: any) => {
    if (!enhancedViewApp?.applicationId) return;
    try {
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emergencyContact }),
      });
      if (res.success && res.candidate) {
        setEnhancedViewApp(res.candidate);
        setCandidates(prev => prev.map(c => c.id === enhancedViewApp.id || c.applicationId === enhancedViewApp.applicationId ? { ...c, ...res.candidate } : c));
        notifyToast('success', 'Emergency contact updated successfully');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save emergency contact');
    }
  };

  const handleAdminDeleteDocument = async (docId: string, docName?: string) => {
    if (!enhancedViewApp?.applicationId || !docId) return;
    // REF-10: no native browser confirmation here. The only call path
    // into this handler is ApplicantDetail's Delete button, which sits
    // inside that component's styled shared-Modal confirmation — it
    // names the document ("Are you sure you want to delete "{name}"?")
    // and states the consequence ("This action cannot be undone.")
    // before invoking this handler with (docId, name). Prompting again
    // here would double-confirm every delete; the native dialog that
    // used to sit at this spot is removed, not replaced.
    try {
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/documents/${docId}`, {
        method: 'DELETE',
      });
      if (res.success) {
        notifyToast('success', 'Document deleted successfully');
        fetchEnhancedDocs(enhancedViewApp.applicationId);
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to delete document');
    }
  };

  const handleRenameDocument = async (docId: string, newName: string) => {
    if (!enhancedViewApp?.applicationId || !docId || !newName.trim()) return;
    try {
      const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/documents/${docId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.success) {
        notifyToast('success', 'Document renamed successfully');
        fetchEnhancedDocs(enhancedViewApp.applicationId);
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to rename document');
    }
  };

  const handleDownloadAllAsZip = async (app: any, filter?: 'all' | 'approved' | 'rejected') => {
    let docsToZip = [...enhancedDocs];
    if (filter === 'approved') docsToZip = enhancedDocs.filter(d => d.status === 'Approved');
    else if (filter === 'rejected') docsToZip = enhancedDocs.filter(d => d.status === 'Rejected');

    if (docsToZip.length === 0) {
      notifyToast('warning', 'No documents match the selected filter.');
      return;
    }

    try {
      notifyToast('info', 'Preparing ZIP download...');
      const zip = new JSZip();
      for (const d of docsToZip) {
        const url = await resolveFirebaseDocumentUrl(d.storagePath, d.downloadUrl).catch(() => d.downloadUrl || d.storagePath || '');
        if (!url) continue;
        try {
          const resp = await fetch(url);
          if (resp.ok) {
            const blob = await resp.blob();
            const fileName = d.name || d.fileName || `${d.type || 'Document'}.pdf`;
            zip.file(fileName, blob);
          }
        } catch (fErr) {
          console.error('Failed fetching file for zip:', d.name, fErr);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const applicantName = `${app.firstName || ''}_${app.lastName || ''}`.replace(/\s+/g, '_') || 'Applicant';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${applicantName}_Documents.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      notifyToast('success', 'ZIP downloaded successfully');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to download ZIP file');
    }
  };

  const fetchEnhancedComments = async (appId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(withTeamSelection(`/api/referrer/applications/${appId}/comments`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setEnhancedComments(data.comments || []);
      }
    } catch { /* ignore */ }
  };

  const fetchReferrerEvents = async () => {
    try {
      const evData = await safeFetch('/api/referrer/events');
      const rawEvs = evData.events || [];
      setCalendarEvents(rawEvs.filter((e: any) => !e.archived && e.status !== 'Archived'));
    } catch (err) {
      console.error('Failed to fetch referrer events:', err);
    }
  };

  const populateCandidateEditStates = (app: any) => {
    setEditAssignmentEmployer(app.assignment?.employerName || '');
    setEditAssignmentRole(app.assignment?.roleTitle || '');
    setEditAssignmentCity(app.assignment?.city || '');
    setEditAssignmentState(app.assignment?.state || '');
    setEditAssignmentCountry(app.assignment?.country || 'United States');

    setEditVisaNo(app.visaDetails?.visaNumber || app.visaDetails?.visaApplicationNumber || '');
    setEditVisaStatus(app.visaDetails?.visaApprovalStatus || 'Pending');
    setEditVisaDate(app.visaDetails?.embassyAppointmentDate || '');
    setEditVisaLocation(app.visaDetails?.embassyLocation || '');

    setEditFlightAirline(app.flightDetails?.airline || '');
    setEditFlightNo(app.flightDetails?.flightNumber || '');
    setEditFlightDepDateTime(app.flightDetails?.departureDateTime || '');
    setEditFlightArrDateTime(app.flightDetails?.arrivalDateTime || '');
    setEditFlightDepAirport(app.flightDetails?.departureAirport || '');
    setEditFlightArrAirport(app.flightDetails?.arrivalAirport || '');
    setEditFlightNotes(app.flightDetails?.additionalNotes || '');

    setEditEmbassyDate(app.embassyDetails?.embassyAppointmentDate || '');
    setEditEmbassyLocation(app.embassyDetails?.embassyLocation || '');
    setEditEmbassyResult(app.embassyDetails?.interviewResult || 'Pending');

    setEditDs160No(app.ds160Details?.ds160Number || app.ds160Details?.ds160ConfirmationNumber || '');
    setEditDs160Status(app.ds160Details?.ds160SubmissionStatus || 'Pending');

    setEditPassportNo(app.passportInfo?.passportNumber || app.passport?.passportNumber || '');
    setEditPassportCountry(app.passportInfo?.countryOfIssue || app.passport?.countryOfIssue || '');
    setEditPassportIssue(app.passportInfo?.issueDate || app.passport?.issueDate || '');
    setEditPassportExpiry(app.passportInfo?.expirationDate || app.passport?.expirationDate || '');
    setEditPassVisaStatus(app.passportInfo?.visaStatus || app.passport?.visaStatus || '');
    setEditDs2019(app.passportInfo?.ds2019Number || app.passport?.ds2019Number || '');
    setEditSevisId(app.passportInfo?.sevisId || app.passport?.sevisId || '');
    setEditIntendedArrival(app.passportInfo?.intendedArrivalDate || app.passport?.intendedArrivalDate || '');
    setEditUsDestination(app.passportInfo?.usDestinationAddress || app.passport?.usDestinationAddress || '');

    setEditProgram(app.programId || app.programType || '');
    setEditProgramTerm(app.programTerm || '');
    setEditProgStart(app.programPreferences?.startDate || '');
    setEditProgDuration(app.programPreferences?.duration || '');
    setEditProgState(app.programPreferences?.preferredState || '');
    setEditProgIndustry(app.programPreferences?.industry || '');
    setEditProgEnglish(app.programPreferences?.englishLevel || '');
    setEditProgSkills(app.programPreferences?.skills || '');
    setEditProgRelocate(app.programPreferences?.relocationPreference || '');
    setEditProgNotes(app.programPreferences?.notes || '');
  };

  const openSlideIn = async (c: any) => {
    const targetId = c.id || c.applicationId;
    if (enhancedPermission) {
      // Instantly render view with available candidate info
      setEnhancedViewApp(c);
      populateCandidateEditStates(c);
      setView('enhanced-view');
      fetchEnhancedDocs(targetId);
      fetchEnhancedComments(targetId);
      fetchReferrerEvents();

      // Background fetch for complete detailed object
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(withTeamSelection(`/api/referrer/candidates/${targetId}`), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          const app = data.candidate || c;
          setEnhancedViewApp(app);
          populateCandidateEditStates(app);
        }
      } catch { /* use initial candidate data */ }
      return;
    }

    // Standard Slide-in view
    setSlideInCandidate(c);
    setSlideInLoading(false);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(withTeamSelection(`/api/referrer/candidates/${targetId}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setSlideInCandidate(data.candidate);
      }
    } catch { /* use initial candidate data */ }
  };

  // Chat §6: the inbox/thread hand over an application ID ONLY. Resolve
  // it against the dashboard's candidate list (seed for instant render)
  // and let openSlideIn re-fetch the CURRENT record by id — the chat
  // surface never passes its own possibly-stale copy of the record, and
  // the render target is this same candidate view Candidates uses.
  const openCandidateFromChat = (applicationId: string) => {
    if (!applicationId) return;
    const found = candidates.find((c: any) => c.id === applicationId || c.applicationId === applicationId);
    void openSlideIn(found ? { ...found, id: applicationId } : { id: applicationId });
  };

  const closeSlideIn = () => {
    setSlideInCandidate(null);
  };

  // Sync enhancedViewApp or slideInCandidate with URL path
  useEffect(() => {
    if (currentRoute.type === 'applications_detail') {
      const targetId = currentRoute.id;
      const found = candidates.find(c => c.id === targetId || c.applicationId === targetId);
      if (found) {
        if (enhancedPermission) {
          if (enhancedViewApp?.id !== targetId && enhancedViewApp?.applicationId !== targetId) {
            openSlideIn(found);
          }
        } else {
          if (slideInCandidate?.id !== targetId) {
            openSlideIn(found);
          }
        }
      }
    } else {
      if (currentPath === '/applications' || currentPath === '/candidates') {
        if (enhancedViewApp) {
          setEnhancedViewApp(null);
          setView('candidates');
        }
        if (slideInCandidate) {
          setSlideInCandidate(null);
        }
      }
    }
  }, [currentPath, candidates, enhancedPermission]);

  // Sync URL path when enhancedViewApp or slideInCandidate changes
  useEffect(() => {
    if (enhancedPermission && enhancedViewApp) {
      navigate(`/applications/adminview/${enhancedViewApp.applicationId || enhancedViewApp.id}`);
    } else if (!enhancedPermission && slideInCandidate) {
      navigate(`/applications/adminview/${slideInCandidate.id}`);
    } else if (currentRoute.type === 'applications_detail') {
      navigate('/candidates');
    }
  }, [enhancedViewApp, slideInCandidate]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    notifyToast(type, message);
  };

  const handleCopyLink = async () => {
    // PR-13b: the copy buttons render disabled while restricted; never
    // copy from a stale handler if one fires anyway.
    if (restricted) return;
    try {
      await navigator.clipboard.writeText(effectiveReferralLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      showToast('Referral link copied to clipboard');
    } catch {
      showToast('Failed to copy link', 'error');
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    // PR-13b courtesy refusal (mirrors handleAddCandidate): the form
    // renders disabled while restricted; refuse here too rather than
    // round-tripping to the server for the same answer.
    if (restricted) { setInviteError(REFERRER_RESTRICTED_NOTICE); return; }
    const emailToCheck = inviteEmail.trim();
    if (!emailToCheck) return;
    setValidatingEmail(true);
    setInviteError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const checkRes = await fetch('/api/referrer/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailToCheck }),
      });
      if (!checkRes.ok) throw new Error('Failed to validate email');
      const checkData = await checkRes.json();
      if (checkData.exists) {
        setInviteError(checkData.message || 'This email belongs to an existing applicant. You cannot send an invitation to them.');
        setValidatingEmail(false);
        return;
      }
      const inviteRes = await fetch(withTeamSelection('/api/referrer/invitations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailToCheck, message: inviteMessage }),
      });
      const inviteData = await inviteRes.json().catch(() => ({}));
      if (!inviteRes.ok) {
        noteTeamSelectionFailure('/api/referrer/invitations', inviteRes.status, inviteData);
        throw new Error(inviteData.message || inviteData.error || 'Unable to send invitation');
      }
      if (inviteData.invitation) setInvitations(prev => [inviteData.invitation, ...prev]);
      setInviteEmail('');
      setInviteMessage('');
      showToast('Invitation sent successfully');
    } catch (err: any) {
      setInviteError(err.message || 'Validation failed. Please try again.');
    } finally {
      setValidatingEmail(false);
    }
  };

  // §2.6: Resend is a real action — it re-issues the invitation through the
  // same endpoint as a fresh send (the old button only showed a toast).
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const handleResendInvitation = async (inv: any) => {
    // PR-13b courtesy refusal: resend buttons render disabled while restricted.
    if (restricted) { showToast(REFERRER_RESTRICTED_NOTICE, 'error'); return; }
    if (!inv?.email || resendingInviteId) return;
    setResendingInviteId(inv.id || inv.email);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(withTeamSelection('/api/referrer/invitations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: inv.email, message: inv.message || '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        noteTeamSelectionFailure('/api/referrer/invitations', res.status, data);
        throw new Error(data.message || data.error || 'Unable to resend invitation');
      }
      if (data.invitation) setInvitations(prev => [data.invitation, ...prev]);
      showToast('Invitation resent to ' + inv.email);
    } catch (err: any) {
      showToast(err.message || 'Failed to resend invitation', 'error');
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(withTeamSelection('/api/referrer/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: profileForm.company || profileForm.firstName + ' ' + profileForm.lastName,
          contactName: `${profileForm.firstName} ${profileForm.lastName}`.trim(),
          contactPhone: profileForm.phone,
          taxId: profileForm.taxId,
          bankAccount: profileForm.bankAccount,
          bankRouting: profileForm.bankRouting,
          country: profileForm.country,
          address: profileForm.address,
          website: profileForm.website,
        }),
      });
      if (!res.ok) throw new Error('Failed to save profile');
      showToast('Profile saved successfully', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to save profile', 'error');
    }
  };

  const resetCandidateForm = () => setCandidateForm({ firstName: '', lastName: '', email: '', phone: '', country: '', notes: '', programType: '', programName: '', programTerm: '', sendInvite: false });

  const handleAddCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    // PR-13b: restricted referrers cannot add candidates (the server
    // refuses too — this is the courtesy layer).
    if (restricted) {
      setEmailCheckMsg(REFERRER_RESTRICTED_NOTICE);
      return;
    }
    if (!candidateForm.programType) {
      setEmailCheckMsg('Please select a program.');
      return;
    }
    if (!candidateForm.programTerm) {
      setEmailCheckMsg('Please select a program term.');
      return;
    }
    setAddingCandidate(true);
    // Reset email check message
    setEmailCheckMsg('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(withTeamSelection('/api/referrer/candidates'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...candidateForm }),
      });
      const data = await res.json();
      if (!res.ok) {
        noteTeamSelectionFailure('/api/referrer/candidates', res.status, data);
        // All server failures surface inline in the form (duplicates, the
        // PR-0b unset-commission-rate Gate 2, program errors) — never as a
        // toast that can be missed or linger across navigation.
        setEmailCheckMsg(data.message || data.error || 'Failed to add candidate. Please try again.');
        setAddingCandidate(false);
        return;
      }
      // Add the new candidate to the list, then show the §2.3 success panel
      // (applicant ID + what happens next) instead of silently closing.
      setCandidates(prev => [data.candidate, ...prev]);
      setAddSuccess({ candidate: data.candidate, sendInvite: !!candidateForm.sendInvite });
    } catch (err: any) {
      setEmailCheckMsg(err.message || 'Failed to add candidate. Please try again.');
    }
    setAddingCandidate(false);
  };

  const toggleSaveJob = (jobId: string) => {
    setSavedJobIds(prev =>
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
  };

  // ── Filtered + Paginated data ──────────────────────────────────────
  const filteredJobs = useMemo(() => {
    let list = jobs;
    // Program drill-down: when a program card is selected, scope to it.
    if (jobsProgram) {
      list = list.filter((j: any) => {
        const prog = j.program || j.programType || (j.programs && j.programs[0]) || '';
        return prog === jobsProgram;
      });
    }
    if (!jobSearch.trim()) return list;
    const q = jobSearch.toLowerCase();
    return list.filter((j: any) =>
      (j.title || '').toLowerCase().includes(q) ||
      (j.companyName || '').toLowerCase().includes(q) ||
      (j.location || '').toLowerCase().includes(q)
    );
  }, [jobs, jobSearch, jobsProgram]);
  const paginatedJobs = useMemo(() => {
    const start = (jobPage - 1) * JOBS_PER_PAGE;
    return filteredJobs.slice(start, start + JOBS_PER_PAGE);
  }, [filteredJobs, jobPage]);
  const totalJobPages = Math.max(1, Math.ceil(filteredJobs.length / JOBS_PER_PAGE));

  const filteredCandidates = useMemo(() => {
    if (!candidateSearch.trim()) return candidates;
    const q = candidateSearch.toLowerCase();
    return candidates.filter((c: any) =>
      (c.firstName || '').toLowerCase().includes(q) ||
      (c.lastName || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.stage || '').toLowerCase().includes(q)
    );
  }, [candidates, candidateSearch]);

  const activeCandidates = useMemo(() => candidates.filter((c: any) => c.status !== 'archived'), [candidates]);
  const archivedCandidates = useMemo(() => candidates.filter((c: any) => c.status === 'archived'), [candidates]);

  // ── Views ───────────────────────────────────────────────────────────

  const OverviewView = () => (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Referrer Dashboard</h1>
          <p className="text-sm text-slate-500">Welcome back, {userProfile?.firstName || 'Partner'}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        <KpiCard label="Total Referrals" value={stats.totalReferrals} icon={Users} isHero={true} />
        <KpiCard label="Active Candidates" value={stats.activeCandidates} icon={UserPlus} />
        <KpiCard label="Interviewing" value={stats.interviewing} icon={MessageCircle} />
        <KpiCard label="Placed" value={stats.placed} icon={CheckCircle} />
        <KpiCard label="Rejected" value={stats.rejected} icon={XCircle} />
        <KpiCard label="Offers Accepted" value={stats.offersAccepted} icon={BadgeCheck} />
        <KpiCard label="Visa Process" value={stats.visaProcess} icon={Landmark} />
        <KpiCard label="Traveling" value={stats.traveling} icon={Plane} />
        <KpiCard label="Program Starts" value={stats.programStarts} icon={GraduationCap} />
        <KpiCard label="Total Earnings" value={`$${stats.totalEarnings.toLocaleString()}`} icon={DollarSign} />
        <KpiCard label="Pending" value={`$${stats.pendingCommissions.toLocaleString()}`} icon={Clock} />
        <KpiCard label="Paid" value={`$${stats.paidCommissions.toLocaleString()}`} icon={Wallet} />
      </div>

      {/* Referral link quick copy */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-700">Your Referral Link</p>
          <p className="text-[11px] text-slate-500 truncate font-mono">{effectiveReferralLink}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => { navigator.clipboard.writeText(effectiveReferralLink).catch(() => {}); }}
            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition cursor-pointer"
          >
            Copy Link
          </button>
          <button
            onClick={() => setView('referral-links')}
            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-brand hover:opacity-90 text-white transition cursor-pointer"
          >
            Referral Links
          </button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setView('candidates')}
          className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100 hover:ring-blue-200 hover:shadow-md transition text-left cursor-pointer"
        >
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg"><UserPlus className="h-5 w-5 text-brand" /></div>
            <h3 className="font-semibold text-slate-900">Add Candidate</h3>
          </div>
          <p className="text-xs text-slate-500">Register a new candidate and track their progress.</p>
        </button>
        <button
          onClick={() => setView('referral-links')}
          className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100 hover:ring-blue-200 hover:shadow-md transition text-left cursor-pointer"
        >
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-purple-50 rounded-lg"><Link className="h-5 w-5 text-purple-600" /></div>
            <h3 className="font-semibold text-slate-900">Copy Referral Link</h3>
          </div>
          <p className="text-xs text-slate-500">Share your unique link to auto-assign referrals.</p>
        </button>
        <button
          onClick={() => setView('invitations')}
          className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100 hover:ring-blue-200 hover:shadow-md transition text-left cursor-pointer"
        >
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-amber-50 rounded-lg"><Mail className="h-5 w-5 text-amber-600" /></div>
            <h3 className="font-semibold text-slate-900">Send Invitation</h3>
          </div>
          <p className="text-xs text-slate-500">Invite candidates to apply through the portal.</p>
        </button>
      </div>

      {/* Recent candidates + upcoming calendar side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Candidates</h3>
            <button onClick={() => setView('candidates')} className="text-xs text-brand font-semibold hover:underline cursor-pointer">View All</button>
          </div>
          <div className="space-y-3">
            {candidates.slice(0, 5).map((c: any) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-brand text-xs font-bold">
                    {(c.firstName || '?')[0]}{(c.lastName || '?')[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{c.firstName || ''} {c.lastName || ''}</p>
                    <p className="text-[11px] text-slate-500">{candidateStageLabel(c)} • {c.daysInStage || 0} days</p>
                  </div>
                </div>
                <StageBadge candidate={c} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Upcoming Events</h3>
            <button onClick={() => setView('calendar')} className="text-xs text-brand font-semibold hover:underline cursor-pointer">View Calendar</button>
          </div>
          <div className="space-y-3">
            {calendarEvents.slice(0, 5).map((e: any) => (
              <div key={e.id} className="flex items-center space-x-3 p-3 rounded-xl bg-slate-50">
                <div className="p-2 bg-amber-50 rounded-lg"><Calendar className="h-4 w-4 text-amber-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{e.title}</p>
                  <p className="text-[11px] text-slate-500">{e.date} • {e.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const JobsView = () => {
    const handleReferCandidatesSubmit = async () => {
      // PR-13b courtesy refusal: the refer controls render disabled while restricted.
      if (restricted) { notifyToast('error', REFERRER_RESTRICTED_NOTICE); return; }
      if (!selectedJobDetails || referCandidateIds.length === 0) return;
      setReferringLoader(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(withTeamSelection(`/api/referrer/jobs/${selectedJobDetails.id}/refer`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ candidateIds: referCandidateIds })
        });
        const data = await res.json();
        if (res.ok) {
          notifyToast('success', `Successfully referred ${data.successCount} candidate(s)!`);
          setReferCandidateIds([]);
          // Refresh referred job applications list
          const jaData = await safeFetch('/api/referrer/job-applications');
          setReferrerJobApplications(jaData.applications || []);
        } else {
          noteTeamSelectionFailure('/api/referrer/jobs', res.status, data);
          notifyToast('error', data.error || 'Failed to refer candidates');
        }
      } catch (err: any) {
        console.error(err);
        notifyToast('error', 'Referral submission failed. Please try again.');
      } finally {
        setReferringLoader(false);
      }
    };

    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Job Board</h1>
          <p className="text-sm text-slate-500">
            {jobsProgram ? jobsProgram : 'Browse available J-1 positions and match candidates'}
          </p>
        </div>
        {jobsProgram === null ? (
          <ProgramCardGrid
            programs={jobProgramCards}
            loading={loading}
            onSelect={(prog) => { setJobsProgram(prog); setJobPage(1); }}
            emptyHint="New programs are added as employers post jobs."
          />
        ) : (
        <>
        <button
          type="button"
          onClick={() => { setJobsProgram(null); setJobPage(1); }}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:text-brand/80 transition-colors cursor-pointer"
          aria-label="Back to all programs"
        >
          <ChevronRight className="h-4 w-4 rotate-180" aria-hidden="true" />
          All Programs
        </button>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={jobSearch}
              onChange={e => { setJobSearch(e.target.value); setJobPage(1); }}
              aria-label="Search jobs by title, company, or location"
              placeholder="Search jobs by title, company, or location..."
              className="w-full pl-10 pr-4 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-white shadow-sm"
            />
          </div>
          <div className="flex items-center space-x-1 bg-slate-100 p-1 rounded-xl shrink-0">
            <button
              type="button"
              onClick={() => setJobViewMode('card')}
              className={`px-3 py-3.5 md:py-2 text-xs font-bold rounded-lg transition cursor-pointer flex items-center space-x-1.5 ${jobViewMode === 'card' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span>Cards</span>
            </button>
            <button
              type="button"
              onClick={() => setJobViewMode('list')}
              className={`px-3 py-3.5 md:py-2 text-xs font-bold rounded-lg transition cursor-pointer flex items-center space-x-1.5 ${jobViewMode === 'list' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <List className="h-3.5 w-3.5" />
              <span>List</span>
            </button>
          </div>
        </div>

        {jobViewMode === 'list' ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Job Title</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Program</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Referred</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedJobs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">No jobs available at the moment.</td>
                    </tr>
                  ) : (
                    paginatedJobs.map((job: any) => {
                      const referredCount = referrerJobApplications.filter((ja: any) => ja.jobId === job.id).length;
                      return (
                        <tr
                          key={job.id}
                          onClick={() => { setSelectedJobDetails(job); setReferCandidateIds([]); }}
                          className="hover:bg-slate-50/80 transition cursor-pointer"
                        >
                          <td className="px-4 py-3.5 font-extrabold text-slate-900">{job.title}</td>
                          <td className="px-4 py-3.5 font-medium text-slate-700">{job.companyName}</td>
                          <td className="px-4 py-3.5 text-slate-500">{job.location}</td>
                          <td className="px-4 py-3.5"><span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[10px] font-bold">{job.program}</span></td>
                          <td className="px-4 py-3.5"><span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[10px] font-bold">{job.jobType}</span></td>
                          <td className="px-4 py-3.5">
                            {referredCount > 0 ? (
                              <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 rounded-md text-[10px] font-bold border border-purple-100">
                                {referredCount} Referred
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            {/* REF-11: an explicit focusable control per
                                row, not row-click only. */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedJobDetails(job); setReferCandidateIds([]); }}
                              className="font-bold text-brand hover:underline cursor-pointer px-2 py-2 -mx-2 rounded-lg"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedJobs.length === 0 ? (
            <div className="col-span-full bg-white rounded-2xl p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-100">
              <Briefcase className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No jobs available at the moment.</p>
            </div>
          ) : (
            paginatedJobs.map((job: any) => {
              const isSaved = savedJobIds.includes(job.id);
              const referredCount = referrerJobApplications.filter((ja: any) => ja.jobId === job.id).length;
              return (
                <div
                  key={job.id}
                  onClick={() => { setSelectedJobDetails(job); setReferCandidateIds([]); }}
                  className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100 hover:shadow-md transition flex flex-col border border-slate-200/50 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3" onClick={e => e.stopPropagation()}>
                    <div className="p-2 bg-blue-50 rounded-lg"><Briefcase className="h-5 w-5 text-brand" /></div>
                    <button onClick={() => toggleSaveJob(job.id)} className="p-1.5 rounded-lg hover:bg-slate-100 transition cursor-pointer">
                      <Star className={`h-4 w-4 ${isSaved ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
                    </button>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{job.title}</h3>
                  <p className="text-xs text-slate-500 mb-3">{job.companyName} • {job.location}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-semibold">{job.jobType}</span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-semibold">{job.program}</span>
                    {referredCount > 0 && (
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md text-[10px] font-bold border border-purple-100">
                        {referredCount} Referred
                      </span>
                    )}
                  </div>
                  <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span>Available Slots: {job.positions || 1}</span>
                    <span className="font-semibold text-brand hover:underline">View Details</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}
        {/* Pagination */}
        {filteredJobs.length > JOBS_PER_PAGE && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <button
              onClick={() => setJobPage(p => Math.max(1, p - 1))}
              disabled={jobPage === 1}
              className="px-3 py-1.5 min-h-[44px] sm:min-h-[32px] inline-flex items-center justify-center text-xs font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              Previous
            </button>
            <span className="text-xs font-semibold text-slate-600 px-3">
              {jobPage} of {totalJobPages}
            </span>
            <button
              onClick={() => setJobPage(p => Math.min(totalJobPages, p + 1))}
              disabled={jobPage === totalJobPages}
              className="px-3 py-1.5 min-h-[44px] sm:min-h-[32px] inline-flex items-center justify-center text-xs font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              Next
            </button>
          </div>
        )}

        {/* JOB DETAILS MODAL */}
        {selectedJobDetails && (() => {
          const job = selectedJobDetails;
          // Filter candidate matches / applications for this job from referrerJobApplications
          const previouslyReferred = referrerJobApplications.filter((ja: any) => ja.jobId === job.id);

          // Get list of eligible candidates based on Program match
          const eligibleCandidates = (candidates || []).filter(c => {
            const candProgram = (c.programType || c.programName || c.programId || '').toLowerCase().trim();
            const jobProgram = (job.program || job.programType || '').toLowerCase().trim();
            const jobPrograms = (job.programs || []).map((p: string) => p.toLowerCase().trim());
            if (jobPrograms.length > 0) {
              return jobPrograms.some((p: string) => candProgram === p || candProgram.includes(p) || p.includes(candProgram));
            }
            return candProgram === jobProgram || candProgram.includes(jobProgram) || jobProgram.includes(candProgram);
          });

          return (
            <Modal onClose={() => setSelectedJobDetails(null)} ariaLabel="Job Details">
              <div className="relative w-full max-w-3xl bg-white overflow-y-auto max-h-[90dvh]">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
                  <div>
                    <h2 className="text-base font-extrabold text-slate-900">{job.title}</h2>
                    <p className="text-xs text-slate-500 font-semibold">{job.companyName} • {job.location}</p>
                  </div>
                  <button onClick={() => setSelectedJobDetails(null)} aria-label="Close" className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg cursor-pointer">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Job Stats & Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { label: 'Available Slots', value: job.positions || 1 },
                      { label: 'Required J-1 Program', value: job.program || job.programType || 'Any' },
                      { label: 'Previously Referred', value: previouslyReferred.length },
                    ].map((s, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                        <p className="text-[10px] text-slate-400 uppercase font-bold">{s.label}</p>
                        <p className="text-sm font-bold text-slate-800 mt-1">{s.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Position Description</h4>
                    <p className="text-xs text-slate-700 leading-relaxed bg-slate-50/50 p-3 rounded-xl border border-slate-100 whitespace-pre-wrap">
                      {job.description || 'No position description provided.'}
                    </p>
                  </div>

                  {/* Refer/Match candidates section */}
                  <div className="space-y-3 border-t border-slate-200 pt-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Refer Eligible Candidates</h4>
                    {restricted && (
                      <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                        Referring is paused while your account is restricted — you can still browse jobs and see who you referred before.
                      </p>
                    )}
                    {eligibleCandidates.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">No candidates match this job's required J-1 program.</p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[10px] text-slate-500 font-semibold mb-2">Select eligible candidate(s) to submit interest on their behalf:</p>
                        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/30">
                          {eligibleCandidates.map((c: any) => {
                            const timesReferred = previouslyReferred.filter((ja: any) => ja.applicationId === c.applicationId).length;
                            const isReferredAlready = timesReferred >= 1;
                            return (
                              <label key={c.id} className="flex items-center gap-3 py-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  disabled={isReferredAlready || restricted}
                                  title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
                                  checked={referCandidateIds.includes(c.applicationId)}
                                  onChange={() => {
                                    setReferCandidateIds(prev =>
                                      prev.includes(c.applicationId)
                                        ? prev.filter(id => id !== c.applicationId)
                                        : [...prev, c.applicationId]
                                    );
                                  }}
                                  className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                                />
                                <div className="text-xs">
                                  {/* PR-16 / D-25: name may live only in personalDetails — resolve via the shared helper */}
                                  <span className="font-semibold text-slate-800">{firstUsablePersonName([c], '')}</span>
                                  <span className="text-[10px] text-slate-500 ml-2 font-mono">({c.applicantId || c.id})</span>
                                  {timesReferred >= 1 && (
                                    <span className="text-[11px] bg-red-50 text-red-700 border border-red-100 px-1.5 py-0.5 rounded font-bold ml-2">Already Referred (Blocked)</span>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        {referCandidateIds.length > 0 && (
                          <div className="flex justify-end pt-1">
                            <button
                              onClick={handleReferCandidatesSubmit}
                              disabled={referringLoader || restricted}
                              title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
                              className="px-4 py-2 bg-brand hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition flex items-center space-x-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {referringLoader ? 'Submitting...' : `Refer Selected Candidates (${referCandidateIds.length})`}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* List of referred candidates for this job */}
                  <div className="space-y-3 border-t border-slate-200 pt-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Previously Referred Candidates</h4>
                    {previouslyReferred.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">No candidates referred by you to this position yet.</p>
                    ) : (
                      <div className="border border-slate-200 rounded-xl overflow-hidden">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                            <tr>
                              <th className="px-3 py-2">Name</th>
                              <th className="px-3 py-2">Referral Date</th>
                              <th className="px-3 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {previouslyReferred.map((ja: any) => (
                              <tr key={ja.id} className="text-slate-700 hover:bg-slate-50">
                                <td className="px-3 py-2 font-bold">{ja.applicantName}</td>
                                <td className="px-3 py-2 text-slate-500">{new Date(ja.appliedAt || ja.createdAt).toLocaleDateString()}</td>
                                <td className="px-3 py-2">
                                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-100">
                                    REFERRED
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Modal>
          );
        })()}
        </>
        )}
      </div>
    );
  };


  const CommissionsView = () => {
    const filtered = filterCommissionRows(commissions, commSearch, commStatusFilter);

    const totalPages = Math.max(1, Math.ceil(filtered.length / COMM_PER_PAGE));
    const paginatedCommissions = filtered.slice((commPage - 1) * COMM_PER_PAGE, commPage * COMM_PER_PAGE);

    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Commission Tracking</h1>
            <p className="text-sm text-slate-500">Track candidate commissions — amounts stamped at referral — and their eligibility statuses</p>
          </div>
        </div>

        {/* Earnings summary cards — same buckets/totals as the dashboard
            card and My Commissions (§2.4, lib/referrerCommissions) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(() => {
            const summary = summarizeCommissions(commissions);
            return [
              { label: 'Paid Out', value: `$${summary.paid.toLocaleString()}`, icon: Wallet, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Payable (Queued)', value: `$${summary.payable.toLocaleString()}`, icon: DollarSign, color: 'text-brand', bg: 'bg-blue-50' },
              { label: 'Accruing', value: `$${summary.accruing.toLocaleString()}`, icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
              { label: 'Total Candidates', value: commissions.length, icon: Users, color: 'text-slate-600', bg: 'bg-slate-50' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100">
                <div className={`p-2 rounded-lg ${s.bg} w-fit mb-2`}><s.icon className={`h-4 w-4 ${s.color}`} /></div>
                <p className="text-lg font-bold text-slate-900">{s.value}</p>
                <p className="text-[11px] text-slate-500 font-medium">{s.label}</p>
              </div>
            ));
          })()}
        </div>

        {/* Filters & Search Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={commSearch}
                onChange={(e) => { setCommSearch(e.target.value); setCommPage(1); }}
                aria-label="Search commissions by candidate name, email, or program"
                placeholder="Search candidates by name, email, or program..."
                className="w-full pl-10 pr-4 py-3 md:py-2 text-base md:text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs font-bold text-slate-500 shrink-0">Filter Status:</span>
              <select
                value={commStatusFilter}
                onChange={(e) => { setCommStatusFilter(e.target.value); setCommPage(1); }}
                className="px-3 py-3 md:py-2 text-base md:text-xs border border-slate-200 rounded-xl bg-white font-semibold focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              >
                <option value="All">All Statuses</option>
                <option value="Committed">Committed / Eligible / Approved</option>
                <option value="Queued">Queued (Awaiting Payout)</option>
                <option value="Paid">Paid Out / Processed</option>
                <option value="Pending">Not Committed / Pending Review</option>
                <option value="Ineligible">Cancelled / Ineligible</option>
              </select>
            </div>
          </div>
        </div>

        {/* Commissions Table (md and up; below md the card list after
            it presents the same page — REF-05) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3">Program</th>
                  <th className="px-4 py-3">Stage</th>
                  <th className="px-4 py-3">Placed / Hired Status</th>
                  <th className="px-4 py-3">Financial Status</th>
                  <th className="px-4 py-3 text-right">Commission</th>
                  <th className="px-4 py-3 text-center">Commission Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedCommissions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      {commissions.length === 0
                        ? 'No commissions yet — a commission appears here as soon as a referred candidate starts their application, stamped at the rate on the referrer profile for their program.'
                        : 'No commission records match your search or filter.'}
                    </td>
                  </tr>
                ) : (
                  paginatedCommissions.map((c: any) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedCommission(c)}
                      className="hover:bg-slate-50/80 transition cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{c.candidateName}</p>
                        <p className="text-[11px] text-slate-500">{c.candidateEmail || 'No email'}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{c.program}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="px-2 py-0.5 rounded bg-slate-100 font-bold text-slate-700">
                          {candidateStageLabel(c)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${placementChipClass(c)}`}>
                          {c.placementStatus || 'Unassigned'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${financeChipClass(c)}`}>
                          {c.financeStatusSummary || c.financeStatus || 'No Invoices'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">
                        ${commissionAmount(c).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold ${commissionStatusChipClass(c)}`}>
                          {c.status || c.eligibility || 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSelectedCommission(c); }}
                          className="px-2.5 py-1 min-h-[32px] text-[11px] font-bold text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition cursor-pointer flex items-center gap-1 ml-auto"
                        >
                          <Eye className="h-3.5 w-3.5" /> Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (< md) — REF-05: the same page of commissions
              as the table, stacked with every table field, no horizontal
              scroll. The whole card is the tap target and opens the same
              CommissionDetailModal as the table row tap. */}
          <div className="md:hidden divide-y divide-slate-100">
            {paginatedCommissions.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <p className="text-sm text-slate-500">
                  {commissions.length === 0
                    ? 'No commissions yet — a commission appears here as soon as a referred candidate starts their application, stamped at the rate on the referrer profile for their program.'
                    : 'No commission records match your search or filter.'}
                </p>
              </div>
            ) : (
              paginatedCommissions.map((c: any) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCommission(c)}
                  className="w-full text-left px-4 py-4 active:bg-slate-50 transition cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate">{c.candidateName}</p>
                      <p className="text-[11px] text-slate-500 truncate">{c.candidateEmail || 'No email'}</p>
                    </div>
                    <p className="font-mono text-base font-bold text-slate-900 shrink-0">
                      ${commissionAmount(c).toLocaleString()}
                    </p>
                  </div>
                  <p className="text-xs text-slate-700 font-medium mt-1.5">{c.program}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="px-2 py-0.5 rounded bg-slate-100 font-bold text-slate-700 text-[11px]">
                      {candidateStageLabel(c)}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${placementChipClass(c)}`}>
                      {c.placementStatus || 'Unassigned'}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${financeChipClass(c)}`}>
                      {c.financeStatusSummary || c.financeStatus || 'No Invoices'}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-extrabold ${commissionStatusChipClass(c)}`}>
                      {c.status || c.eligibility || 'Pending'}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          {filtered.length > COMM_PER_PAGE && (
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <p className="text-[11px] text-slate-500 font-medium">
                Showing {(commPage - 1) * COMM_PER_PAGE + 1} – {Math.min(commPage * COMM_PER_PAGE, filtered.length)} of {filtered.length} records
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCommPage(p => Math.max(1, p - 1))}
                  disabled={commPage <= 1}
                  className="px-2.5 py-1 min-h-[44px] sm:min-h-[32px] text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-40 cursor-pointer transition flex items-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                {/* REF-08: below sm the compact "Page n of N" pattern (the
                    Jobs pager) replaces the number row so the controls
                    fit one line at 360px at any page count; at sm and up
                    the windowed page list renders. REF-17: the active
                    page wears the brand primary, as the Candidates pager
                    does, not purple. */}
                <span className="sm:hidden text-xs font-bold text-slate-600 px-1">Page {commPage} of {totalPages}</span>
                <div className="hidden sm:flex items-center gap-1">
                  {pageWindow(commPage, totalPages).map((item, i) => item === 'ellipsis' ? (
                    <span key={`gap-${i}`} className="px-1 text-xs font-bold text-slate-400" aria-hidden="true">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setCommPage(item)}
                      aria-current={commPage === item ? 'page' : undefined}
                      className={`px-3 py-1 min-h-[32px] min-w-[32px] inline-flex items-center justify-center text-xs font-bold rounded-lg cursor-pointer transition ${
                        commPage === item ? 'btn-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setCommPage(p => Math.min(totalPages, p + 1))}
                  disabled={commPage >= totalPages}
                  className="px-2.5 py-1 min-h-[44px] sm:min-h-[32px] text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-40 cursor-pointer transition flex items-center gap-1"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedCommission && <CommissionDetailModal commission={selectedCommission} onClose={() => setSelectedCommission(null)} />}
      </div>
    );
  };


  // REF-02: a non-super Referrer Team member's commissions destination.
  // Payout data is held at Super Agent level on the server
  // (canViewReferrerCommissions in serverCommissionGates.ts allows only
  // the org owner's uid; a member's /api/referrer/commissions fetch is
  // refused with 403 and no commission rows ever reach this client), so
  // the member's whole presentation is this notice. It must never be
  // the team-wide Commission Tracking table, and never totals computed
  // from the withheld (empty) list, which would read as real $0
  // earnings for members whose referrals do earn the team commission.
  const MemberCommissionsView = () => (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-bold text-slate-900">My Commissions</h1>
        <p className="text-sm text-slate-500">Commissions for candidates referred by your Referrer Team.</p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 text-center py-16 px-6">
        <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm font-bold text-slate-600 mb-1">Held by your Super Agent</p>
        <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
          Candidates you refer still earn commission for your Referrer Team. Commission and payout
          details are visible to your team's Super Agent, who manages payouts for the whole team.
          Contact your Super Agent for payout updates.
        </p>
        <button
          type="button"
          onClick={() => setView('candidates')}
          className="mt-5 px-4 py-2 btn-primary text-white rounded-xl text-xs font-bold transition cursor-pointer"
        >
          View candidates
        </button>
      </div>
    </div>
  );


  // Read-only "My Commissions / Payouts" for non-enhanced referrers.
  // Shows ONLY the caller's own commission data (their referrals, their
  // payouts). No team data, no aggregate analytics, no admin actions
  // (commit/queue/cancel stay admin-only server-side).
  // §2.4: totals come from the shared summarizer (stamped amounts only) so
  // they always match the dashboard payouts card; rows open the same
  // detail modal as Commission Tracking, history included.
  const MyCommissionsView = () => {
    const summary = summarizeCommissions(commissions);
    // REF-19: the same search + status filter + pagination as the
    // enhanced view (shared filterCommissionRows, same COMM_PER_PAGE),
    // governing the table and the mobile cards alike.
    const filtered = filterCommissionRows(commissions, myCommSearch, myCommStatusFilter);
    const totalPages = Math.max(1, Math.ceil(filtered.length / COMM_PER_PAGE));
    const paginatedCommissions = filtered.slice((myCommPage - 1) * COMM_PER_PAGE, myCommPage * COMM_PER_PAGE);

    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-xl font-bold text-slate-900">My Commissions</h1>
          <p className="text-sm text-slate-500">Your commissions for candidates you referred — each amount is the rate stamped when the candidate was referred.</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100">
            <div className="p-2 rounded-lg bg-slate-100 w-fit mb-2"><Clock className="h-4 w-4 text-slate-500" /></div>
            <p className="text-lg font-bold text-slate-900">${summary.accruing.toLocaleString()}</p>
            <p className="text-[11px] text-slate-500 font-medium">Accruing</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100">
            <div className="p-2 rounded-lg bg-blue-50 w-fit mb-2"><BadgeCheck className="h-4 w-4 text-brand" /></div>
            <p className="text-lg font-bold text-slate-900">${summary.payable.toLocaleString()}</p>
            <p className="text-[11px] text-slate-500 font-medium">Payable</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100">
            <div className="p-2 rounded-lg bg-emerald-50 w-fit mb-2"><Wallet className="h-4 w-4 text-emerald-600" /></div>
            <p className="text-lg font-bold text-slate-900">${summary.paid.toLocaleString()}</p>
            <p className="text-[11px] text-slate-500 font-medium">Paid out</p>
          </div>
        </div>

        {/* Filters & Search Bar — REF-19: identical affordances to the
            enhanced Commission Tracking view (same fields searched,
            same status buckets, REF-04 mobile sizing). */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={myCommSearch}
                onChange={(e) => { setMyCommSearch(e.target.value); setMyCommPage(1); }}
                aria-label="Search commissions by candidate name, email, or program"
                placeholder="Search candidates by name, email, or program..."
                className="w-full pl-10 pr-4 py-3 md:py-2 text-base md:text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs font-bold text-slate-500 shrink-0">Filter Status:</span>
              <select
                value={myCommStatusFilter}
                onChange={(e) => { setMyCommStatusFilter(e.target.value); setMyCommPage(1); }}
                className="px-3 py-3 md:py-2 text-base md:text-xs border border-slate-200 rounded-xl bg-white font-semibold focus:outline-none cursor-pointer focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              >
                <option value="All">All Statuses</option>
                <option value="Committed">Committed / Eligible / Approved</option>
                <option value="Queued">Queued (Awaiting Payout)</option>
                <option value="Paid">Paid Out / Processed</option>
                <option value="Pending">Not Committed / Pending Review</option>
                <option value="Ineligible">Cancelled / Ineligible</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3">Program</th>
                  <th className="px-4 py-3">Stage</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedCommissions.length === 0 ? (
                  commissions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center">
                        <Wallet className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-slate-700">No commissions yet</p>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">
                          A commission appears here as soon as a candidate you referred starts their application.
                          Its amount is stamped at referral time from the rate on your profile for their program, and it
                          moves from Accruing to Payable as your candidate progresses.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                        No commission records match your search or filter.
                      </td>
                    </tr>
                  )
                ) : (
                  paginatedCommissions.map((c: any) => (
                    <tr key={c.id} onClick={() => setSelectedCommission(c)} className="hover:bg-slate-50/80 transition cursor-pointer">
                      <td className="px-4 py-3 font-medium text-slate-900">{c.candidateName || 'Candidate'}</td>
                      <td className="px-4 py-3 text-slate-600">{c.program || c.programName || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{candidateStageLabel(c)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                          {c.payoutProcessed || c.status === 'Paid' ? 'Paid' : (c.status || c.eligibility || 'Pending')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        ${commissionAmount(c).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {/* REF-11: an explicit focusable control per row,
                            not row-click only (the below-md cards from
                            batch 3 already give touch a path). */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSelectedCommission(c); }}
                          className="px-2.5 py-1 min-h-[32px] text-[11px] font-bold text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition cursor-pointer inline-flex items-center gap-1 ml-auto"
                        >
                          <Eye className="h-3.5 w-3.5" /> Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (< md) — REF-05: the same page of commissions
              as the table, stacked with every table field, no horizontal
              scroll. The whole card is the tap target and opens the same
              CommissionDetailModal as the table row tap. */}
          <div className="md:hidden divide-y divide-slate-100">
            {paginatedCommissions.length === 0 ? (
              commissions.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <Wallet className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-700">No commissions yet</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">
                    A commission appears here as soon as a candidate you referred starts their application.
                    Its amount is stamped at referral time from the rate on your profile for their program, and it
                    moves from Accruing to Payable as your candidate progresses.
                  </p>
                </div>
              ) : (
                <div className="px-4 py-12 text-center">
                  <p className="text-sm text-slate-500">No commission records match your search or filter.</p>
                </div>
              )
            ) : (
              paginatedCommissions.map((c: any) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCommission(c)}
                  className="w-full text-left px-4 py-4 active:bg-slate-50 transition cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-medium text-slate-900 truncate">{c.candidateName || 'Candidate'}</p>
                    <p className="text-base font-semibold text-slate-900 shrink-0">
                      ${commissionAmount(c).toLocaleString()}
                    </p>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{c.program || c.programName || '—'}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="px-2 py-0.5 rounded bg-slate-100 font-bold text-slate-700 text-[11px]">
                      {candidateStageLabel(c)}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                      {c.payoutProcessed || c.status === 'Paid' ? 'Paid' : (c.status || c.eligibility || 'Pending')}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Pagination Controls — REF-19: same construction and page
              size as the enhanced view, governing table and cards. */}
          {filtered.length > COMM_PER_PAGE && (
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <p className="text-[11px] text-slate-500 font-medium">
                Showing {(myCommPage - 1) * COMM_PER_PAGE + 1} – {Math.min(myCommPage * COMM_PER_PAGE, filtered.length)} of {filtered.length} records
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setMyCommPage(p => Math.max(1, p - 1))}
                  disabled={myCommPage <= 1}
                  className="px-2.5 py-1 min-h-[44px] sm:min-h-[32px] text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-40 cursor-pointer transition flex items-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                {/* REF-08 / REF-17: same windowed construction and brand
                    active page as Commission Tracking above. */}
                <span className="sm:hidden text-xs font-bold text-slate-600 px-1">Page {myCommPage} of {totalPages}</span>
                <div className="hidden sm:flex items-center gap-1">
                  {pageWindow(myCommPage, totalPages).map((item, i) => item === 'ellipsis' ? (
                    <span key={`gap-${i}`} className="px-1 text-xs font-bold text-slate-400" aria-hidden="true">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setMyCommPage(item)}
                      aria-current={myCommPage === item ? 'page' : undefined}
                      className={`px-3 py-1 min-h-[32px] min-w-[32px] inline-flex items-center justify-center text-xs font-bold rounded-lg cursor-pointer transition ${
                        myCommPage === item ? 'btn-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setMyCommPage(p => Math.min(totalPages, p + 1))}
                  disabled={myCommPage >= totalPages}
                  className="px-2.5 py-1 min-h-[44px] sm:min-h-[32px] text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-40 cursor-pointer transition flex items-center gap-1"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedCommission && (
          <CommissionDetailModal commission={selectedCommission} onClose={() => setSelectedCommission(null)} />
        )}
      </div>
    );
  };


  const ReferralLinksView = () => (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Referral Links</h1>
        <p className="text-sm text-slate-500">Share your unique link to automatically assign candidates to you</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">Your Referral Code</h3>
            <p className="text-sm text-slate-500">Anyone who signs up using this code is linked to your account</p>
          </div>
          <div className="px-4 py-2 bg-slate-100 rounded-xl font-mono text-sm font-bold text-slate-700 tracking-wider">
            {effectiveReferralCode}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="referral-link" className="text-xs font-semibold text-slate-600">Referral Link</label>
          <div className="flex items-center space-x-2">
            <input
              id="referral-link"
              readOnly
              value={effectiveReferralLink}
              className={`flex-1 min-w-0 px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl font-mono ${restricted ? 'text-slate-400' : 'text-slate-700'}`}
            />
            <button
              onClick={handleCopyLink}
              disabled={restricted}
              title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
              className="px-4 py-3 btn-primary  text-white rounded-xl text-sm font-semibold transition flex items-center space-x-2 shrink-0 shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
            >
              {copiedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span>{copiedLink ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
          {restricted && (
            <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              This link is paused while your referring is restricted — signups through it will not be linked to you. It becomes active again if an admin restores your referring.
            </p>
          )}
        </div>

        <div className="bg-blue-50 rounded-xl p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-brand flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900">How it works</p>
            <p className="text-xs text-brand mt-1 leading-relaxed">
              When someone clicks your referral link and creates an account, they are automatically linked to you as their referrer. You will be able to track their progress and earn commissions when they are placed with an employer.{restricted ? ' While your referring is restricted, new signups through the link are not linked to you.' : ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );


  const InvitationsView = () => (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Candidate Invitations</h1>
        <p className="text-sm text-slate-500">Invite candidates to apply and track their progress</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">Send New Invitation</h3>
        <form onSubmit={handleSendInvitation} className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="invite-candidate-email" className="text-xs font-semibold text-slate-600">Candidate Email</label>
            <input
              id="invite-candidate-email"
              type="email"
              required
              aria-invalid={!!inviteError}
              aria-describedby={inviteError ? 'invite-email-error' : undefined}
              disabled={validatingEmail || restricted}
              title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
              value={inviteEmail}
              onChange={e => { setInviteEmail(e.target.value); setInviteError(null); }}
              placeholder="candidate@example.com"
              className="w-full px-4 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>
          {validatingEmail && (
            <div className="flex items-center gap-2 text-xs font-bold text-blue-600 animate-pulse py-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              <span>Validating candidate email...</span>
            </div>
          )}
          {inviteError && (
            <div id="invite-email-error" role="alert" className="text-xs text-red-650 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 animate-fade-in">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
              <span className="font-semibold">{inviteError}</span>
            </div>
          )}
          <div className="space-y-1">
            <label htmlFor="invite-personal-message" className="text-xs font-semibold text-slate-600">Personal Message (optional)</label>
            <textarea
              id="invite-personal-message"
              value={inviteMessage}
              disabled={validatingEmail || restricted}
              title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
              onChange={e => setInviteMessage(e.target.value)}
              placeholder={`Hi! I'd like to invite you to apply for J-1 opportunities through ${brandConfig.businessName}...`}
              rows={3}
              className="w-full px-4 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 "
            />
          </div>
          <button
            type="submit"
            disabled={validatingEmail || restricted}
            title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
            className="px-4 py-2.5 btn-primary  text-white rounded-xl text-sm font-semibold transition flex items-center space-x-2 shadow-lg shadow-blue-600/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {validatingEmail ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Validating...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Send Invitation</span>
              </>
            )}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Invitation History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3 hidden md:table-cell">Opened</th>
                <th className="px-4 py-3 hidden md:table-cell">Registered</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invitations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Mail className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-700">No invitations sent yet</p>
                    <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">
                      Invitations you send will appear here with their progress — sent, opened, and registered —
                      so you can follow up with anyone who hasn't responded.
                    </p>
                  </td>
                </tr>
              )}
              {invitations.map((inv: any) => {
                const fmtInviteDate = (v: any) => {
                  if (!v) return '—';
                  const d = new Date(v);
                  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
                };
                const isTerminalInvite = ['Registered', 'Application Started', 'Application Completed'].includes(inv.status);
                return (
                <tr key={inv.id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 font-medium text-slate-900">{inv.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      inv.status === 'Application Completed' ? 'bg-green-50 text-green-700' :
                      inv.status === 'Registered' || inv.status === 'Application Started' ? 'bg-brand-active' :
                      inv.status === 'Opened' ? 'bg-purple-50 text-purple-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {inv.status || 'Sent'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtInviteDate(inv.sentAt || inv.createdAt)}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{fmtInviteDate(inv.openedAt)}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{fmtInviteDate(inv.registeredAt)}</td>
                  <td className="px-4 py-3">
                    {!isTerminalInvite && (
                      <button
                        onClick={() => handleResendInvitation(inv)}
                        disabled={restricted || resendingInviteId === (inv.id || inv.email)}
                        title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
                        className="inline-flex items-center px-2 py-3.5 md:py-2 -mx-2 rounded-lg text-xs font-semibold text-brand hover:underline cursor-pointer disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                      >
                        {resendingInviteId === (inv.id || inv.email) ? 'Resending…' : 'Resend'}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );


  const TrainingView = () => {
    // Only show programs that have training materials available
    const trainingPrograms = (referrerPrograms || []).filter(p => p.trainingMaterials && p.trainingMaterials.length > 0);
    const selectedProgram = (referrerPrograms || []).find(p => p.id === selectedTrainingProgramId);

    if (selectedTrainingProgramId && selectedProgram) {
      return (
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <div>
              <button
                onClick={() => setSelectedTrainingProgramId(null)}
                className="text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center space-x-1 mb-1 transition cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Back to Programs</span>
              </button>
              <h1 className="text-xl font-bold text-slate-900">{selectedProgram.name} Training</h1>
              <p className="text-sm text-slate-500 font-mono text-xs">Program Code: {selectedProgram.code}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(selectedProgram.trainingMaterials || []).map((m: any) => (
              <div key={m.id} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200/60 flex flex-col justify-between">
                <div>
                  <div className="p-3 bg-blue-50 text-brand rounded-xl w-fit mb-3">
                    {m.type === 'file' ? <FileText className="h-5 w-5" /> : <BookOpen className="h-5 w-5" />}
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm mb-1">{m.name}</h3>
                  <p className="text-xs text-slate-500 capitalize mb-4">{m.type} Resource</p>

                  {m.type === 'text' && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 mb-4 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto font-medium">
                      {m.text}
                    </div>
                  )}
                </div>

                {m.type === 'file' && m.url && (
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 cursor-pointer text-center"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Resource</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Training Center</h1>
          <p className="text-sm text-slate-500">Select one of your assigned programs below to access its training resources</p>
        </div>

        {trainingPrograms.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-12 text-center border border-slate-200">
            <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-700">No training materials currently available</p>
            <p className="text-xs text-slate-500 mt-1">Contact your system administrator to assign resources.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trainingPrograms.map((prog) => (
              <div
                key={prog.id}
                onClick={() => setSelectedTrainingProgramId(prog.id)}
                className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200/70 hover:shadow-md hover:border-brand/40 transition cursor-pointer flex flex-col justify-between"
              >
                <div>
                  <div className="p-3 bg-brand/5 text-brand rounded-xl w-fit mb-4">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-1">{prog.name}</h3>
                  <p className="text-xs text-slate-500 font-mono mb-4">{prog.code}</p>
                </div>
                <button
                  type="button"
                  className="w-full py-2.5 btn-primary  text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Enter Training
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const MarketingView = () => {
    // Only show programs that have marketing materials available
    const marketingPrograms = (referrerPrograms || []).filter(p => p.marketingMaterials && p.marketingMaterials.length > 0);
    const selectedProgram = (referrerPrograms || []).find(p => p.id === selectedMarketingProgramId);

    if (selectedMarketingProgramId && selectedProgram) {
      return (
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <div>
              <button
                onClick={() => setSelectedMarketingProgramId(null)}
                className="text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center space-x-1 mb-1 transition cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Back to Programs</span>
              </button>
              <h1 className="text-xl font-bold text-slate-900">{selectedProgram.name} Marketing Assets</h1>
              <p className="text-sm text-slate-500 font-mono text-xs">Program Code: {selectedProgram.code}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(selectedProgram.marketingMaterials || []).map((m: any) => (
              <div key={m.id} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200/60 flex flex-col justify-between">
                <div>
                  <div className="p-3 bg-purple-50 text-purple-600 rounded-xl w-fit mb-3">
                    {m.type === 'file' ? <FileText className="h-5 w-5" /> : <BookOpen className="h-5 w-5" />}
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm mb-1">{m.name}</h3>
                  <p className="text-xs text-slate-500 capitalize mb-4">{m.type} Material</p>

                  {m.type === 'text' && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 mb-4 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto font-medium">
                      {m.text}
                    </div>
                  )}
                </div>

                {m.type === 'file' && m.url && (
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 cursor-pointer text-center"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Resource</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Marketing Center</h1>
          <p className="text-sm text-slate-500">Select one of your assigned programs below to access its marketing materials</p>
        </div>

        {marketingPrograms.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-12 text-center border border-slate-200">
            <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-700">No marketing materials currently available</p>
            <p className="text-xs text-slate-500 mt-1">Contact your system administrator to assign assets.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {marketingPrograms.map((prog) => (
              <div
                key={prog.id}
                onClick={() => setSelectedMarketingProgramId(prog.id)}
                className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200/70 hover:shadow-md hover:border-brand/40 transition cursor-pointer flex flex-col justify-between"
              >
                <div>
                  <div className="p-3 bg-purple-55 text-purple-650 rounded-xl w-fit mb-4">
                    <Layers className="h-5 w-5 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-1">{prog.name}</h3>
                  <p className="text-xs text-slate-500 font-mono mb-4">{prog.code}</p>
                </div>
                <button
                  type="button"
                  className="w-full py-2.5 btn-primary  text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Enter Marketing
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ProfileView = () => (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Referrer Profile</h1>
        <p className="text-sm text-slate-500">Manage your contact information, payment details, and organization info</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5 space-y-5">
        <h3 className="font-semibold text-slate-900">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="profile-first-name" className="text-xs font-semibold text-slate-600">First Name</label>
            <input id="profile-first-name" value={profileForm.firstName} onChange={e => setProfileForm({ ...profileForm, firstName: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
          <div className="space-y-1">
            <label htmlFor="profile-last-name" className="text-xs font-semibold text-slate-600">Last Name</label>
            <input id="profile-last-name" value={profileForm.lastName} onChange={e => setProfileForm({ ...profileForm, lastName: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
          <div className="space-y-1">
            <label htmlFor="profile-email" className="text-xs font-semibold text-slate-600">Email</label>
            <input id="profile-email" readOnly value={profileForm.email} className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl text-slate-500" />
          </div>
          <div className="space-y-1">
            <label htmlFor="profile-phone" className="text-xs font-semibold text-slate-600">Phone</label>
            <input id="profile-phone" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5 space-y-5">
        <h3 className="font-semibold text-slate-900">Organization Information</h3>
        <div className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="profile-company" className="text-xs font-semibold text-slate-600">Company / Organization Name</label>
            <input id="profile-company" value={profileForm.company} onChange={e => setProfileForm({ ...profileForm, company: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="profile-country" className="text-xs font-semibold text-slate-600">Country</label>
              <select id="profile-country" value={profileForm.country} onChange={e => setProfileForm({ ...profileForm, country: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white">
                <option value="">Select a country...</option>
                {COUNTRIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="profile-website" className="text-xs font-semibold text-slate-600">Website</label>
              <input id="profile-website" value={profileForm.website} onChange={e => setProfileForm({ ...profileForm, website: e.target.value })} placeholder="https://…" className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="profile-address" className="text-xs font-semibold text-slate-600">Address</label>
            <input id="profile-address" value={profileForm.address} onChange={e => setProfileForm({ ...profileForm, address: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
          <div className="space-y-1">
            <label htmlFor="profile-tax-id" className="text-xs font-semibold text-slate-600">Tax ID / EIN</label>
            <input id="profile-tax-id" value={profileForm.taxId} onChange={e => setProfileForm({ ...profileForm, taxId: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5 space-y-5">
        <h3 className="font-semibold text-slate-900">Payment Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="profile-bank-account" className="text-xs font-semibold text-slate-600">Bank Account Number</label>
            <input id="profile-bank-account" value={profileForm.bankAccount} onChange={e => setProfileForm({ ...profileForm, bankAccount: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
          <div className="space-y-1">
            <label htmlFor="profile-bank-routing" className="text-xs font-semibold text-slate-600">Routing Number</label>
            <input id="profile-bank-routing" value={profileForm.bankRouting} onChange={e => setProfileForm({ ...profileForm, bankRouting: e.target.value })} className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5 space-y-5 text-left">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-slate-900">Security</h3>
          <button
            type="button"
            onClick={() => setShowChangePassword(!showChangePassword)}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 cursor-pointer transition uppercase"
          >
            {showChangePassword ? 'Hide Change Password' : 'Change Password'}
          </button>
        </div>

        {showChangePassword && (
          <div className="space-y-4 bg-slate-50 p-4 rounded-2xl border border-slate-200/60 animate-slide-down">
            {changePasswordSuccess ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold px-4 py-3 rounded-xl shadow-sm">
                Password updated successfully!
              </div>
            ) : (
              <>
                {changePasswordError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 text-xs font-bold px-4 py-3 rounded-xl shadow-sm">
                    {changePasswordError}
                  </div>
                )}
                <div className="space-y-1">
                  <label htmlFor="profile-current-password" className="text-xs font-semibold text-slate-600">Current Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      id="profile-current-password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                      aria-pressed={showCurrentPassword}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-3.5 md:p-2 rounded-lg text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <PasswordSetForm
                  onSubmit={async (newPass) => {
                    if (!currentPassword) {
                      setChangePasswordError('Please enter your current password.');
                      return;
                    }
                    setChangePasswordLoading(true);
                    setChangePasswordError('');
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const res = await fetch('/api/auth/change-password', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({ currentPassword, newPassword: newPass, firebaseApiKey: import.meta.env.VITE_FIREBASE_API_KEY || '' }),
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        setChangePasswordError(data.error || 'Failed to update password.');
                        return;
                      }
                      setChangePasswordSuccess(true);
                      setCurrentPassword('');
                    } catch (err: any) {
                      setChangePasswordError('Network error. Please try again.');
                    } finally {
                      setChangePasswordLoading(false);
                    }
                  }}
                  submitLabel="Update Password"
                  loading={changePasswordLoading}
                  showRecoveryEmail={false}
                />
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSaveProfile}
          className="px-6 py-2.5 btn-primary  text-white rounded-xl text-sm font-semibold transition shadow-lg shadow-blue-600/20 cursor-pointer"
        >
          Save Changes
        </button>
      </div>
    </div>
  );

  // ── Main render ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-dvh premium-shell flex items-center justify-center">
        <div className="premium-loader" />
      </div>
    );
  }

  // Enforce enhanced-only and super-only views at render time too — the nav
  // handler and URL initialisation both feed `view`, so gate here as the single
  // source of truth.
  const activeView =
    (!enhancedPermission && ENHANCED_ONLY_VIEWS.has(view)) ||
    (!isSuperAgent && SUPER_ONLY_VIEWS.has(view))
      ? 'dashboard'
      : view;

  return (
    <div className="h-full min-h-0 premium-shell flex flex-col overflow-hidden font-sans">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:p-6 lg:p-8">
      {/* Toasts render via the global root-level notification layer. */}

      {/* Top bar — portal identity + notification bell + profile (consistent across views) */}
      {view !== 'enhanced-view' && (
        <div className="flex items-center justify-between gap-3 mb-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-900 truncate">Referrer Portal</h1>
              <p className="text-xs text-slate-500 truncate">Welcome back, {userProfile?.firstName || 'Partner'}</p>
            </div>
            {/* Multi-team Slice 3: the shared TeamSwitcher — the active
                Referrer Team's name (static for one team, a switch menu
                for several). Renders nothing in the gate's zero-team
                passthrough, leaving this bar exactly as before. */}
            <TeamSwitcher role="referrer" nameClassName="text-sm font-bold text-slate-800 truncate" />
          </div>
          <UserMenu profile={userProfile} email={userEmail} onLogout={onLogout} roleSwitch={roleSwitch} />
        </div>
      )}

      {/* PR-13b: Restricted (deactivated referrer) Banner — read-only mode.
          The exact notice sentence is shared with the server-side
          refusal errors and the re-apply refusal. */}
      {restricted && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center gap-2" role="alert">
          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
          <p className="text-xs font-semibold text-red-800">
            {REFERRER_RESTRICTED_NOTICE} You can still view your candidates and commission history, but referring actions are unavailable.
          </p>
        </div>
      )}

      {/* Pending Approval Banner */}
      {isApproved === false && !restricted && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs font-semibold text-amber-800">
            Your referrer profile is pending approval. Some features are restricted until approved.
          </p>
        </div>
      )}

      {/* Enhanced Permission Banner (shown on non-detail views) */}
      {enhancedPermission && view !== 'enhanced-view' && !bannerDismissed && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-4 flex items-center justify-between gap-2 shadow-sm animate-fade-in">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <p className="text-xs font-semibold text-slate-700">
              Enhanced agency view enabled. Click any candidate to access the full applicant management dashboard.
            </p>
          </div>
          <button
            onClick={() => {
              setBannerDismissed(true);
              if (typeof window !== 'undefined') {
                sessionStorage.setItem('isats_banner_dismissed', 'true');
              }
            }}
            className="p-1 min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition cursor-pointer"
            title="Dismiss banner"
            aria-label="Dismiss banner"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {enhancedPermission && view !== 'enhanced-view' && bannerDismissed && (
        <div className="flex justify-end mb-2">
          <button
            onClick={() => {
              setBannerDismissed(false);
              if (typeof window !== 'undefined') {
                sessionStorage.removeItem('isats_banner_dismissed');
              }
            }}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-indigo-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1 transition cursor-pointer"
            title="Show agency view details"
          >
            <Info className="h-3.5 w-3.5" />
            <span>Enhanced Agency Info</span>
          </button>
        </div>
      )}

      {view === 'enhanced-view' && enhancedViewApp ? (
        <div className="h-full flex flex-col animate-fade-in">
          {/* Back bar */}
          <div className="flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => { setView('candidates'); setEnhancedViewApp(null); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition cursor-pointer shrink-0 whitespace-nowrap"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
                Back to Candidates
              </button>
              <div className="h-5 w-px bg-slate-200 shrink-0" />
              <div className="flex items-center gap-2 min-w-0">
                <Shield className="h-4 w-4 text-purple-600 shrink-0" />
                <span className="text-xs font-bold text-slate-700 min-w-0 truncate">
                  {enhancedViewApp.personalDetails?.firstName || enhancedViewApp.firstName || ''} {enhancedViewApp.personalDetails?.lastName || enhancedViewApp.lastName || ''}
                </span>
                <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                  {enhancedViewApp.applicationId || enhancedViewApp.applicantId || ''}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {enhancedPermission && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-lg text-[10px] font-bold border border-purple-200">
                  <Shield className="h-3 w-3" />
                  Enhanced Agency View
                </span>
              )}
            </div>
          </div>
          {/* Detail view */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ApplicantDetail
              selectedApp={enhancedViewApp}
              appCtx={{
                // ── Tab navigation ──
                adminDetailTab: enhancedDetailTab,
                setAdminDetailTab: (tab: string) => setEnhancedDetailTab(tab),
                handleLazyLoadTab: (tab: string) => {
                  if (tab === 'documents' && enhancedViewApp?.applicationId) {
                    fetchEnhancedDocs(enhancedViewApp.applicationId);
                  }
                  if (tab === 'activity' && enhancedViewApp?.applicationId) {
                    fetchEnhancedComments(enhancedViewApp.applicationId);
                  }
                  if (tab === 'events') {
                    fetchReferrerEvents();
                  }
                },

                // ── Programs ──
                programsList: referrerPrograms,
                programs: [],
                adminDocType: 'profile',
                setAdminDocType: () => {},

                // ── Documents (fetched via server API) ──
                previewDoc,
                setPreviewDoc,
                docsList: enhancedDocs,
                setDocsList: setEnhancedDocs,
                handleReviewDocument: async () => {},
                setRejectionModalDocId: () => {},
                setRejectionReasonInput: () => {},
                handleAdminDeleteDocument,
                handleAdminUploadDocument: async (fileOverride?: File) => {
                  const file = fileOverride || null;
                  if (!file || !enhancedViewApp?.applicationId) return;
                  try {
                    const token = await auth.currentUser?.getIdToken();
                    const reader = new FileReader();
                    const fileBase64 = await new Promise<string>((resolve, reject) => {
                      reader.onload = () => {
                        const result = reader.result as string;
                        resolve(result.split(',')[1] || result);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                    const res = await fetch(withTeamSelection(`/api/referrer/applications/${enhancedViewApp.applicationId}/documents/upload`), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        fileName: file.name,
                        docType: 'profile',
                        fileBase64,
                      }),
                    });
                    const data = await res.json();
                    if (res.ok && data.success) {
                      notifyToast('success', 'Document uploaded successfully');
                      fetchEnhancedDocs(enhancedViewApp.applicationId);
                    } else {
                      noteTeamSelectionFailure('/api/referrer/applications', res.status, data);
                      throw new Error(data.error || 'Upload failed');
                    }
                  } catch (err: any) {
                    console.error('Referrer document upload failed:', err);
                    notifyToast('error', err.message || 'Failed to upload document');
                  }
                },
                handleDeleteDocumentRequest: async () => {},
                handleCreateDocumentRequest: async () => {},
                handleRenameDocument,
                handleDownloadAllAsZip,
                handleRefreshOne: async (docId: string) => {
                  // Refetch all docs to refresh the list
                  if (enhancedViewApp?.applicationId) {
                    fetchEnhancedDocs(enhancedViewApp.applicationId);
                  }
                },
                adminUploadedFile: null,
                setAdminUploadedFile: () => {},

                // ── Comments (Activity Tab) — fetched via server API ──
                newComment: referrerNewComment,
                setNewComment: setReferrerNewComment,
                comments: enhancedComments,
                setComments: setEnhancedComments,
                handleAddComment: async () => {
                  if (!auth.currentUser || !enhancedViewApp?.applicationId) return;
                  if (!referrerNewComment.trim()) return;
                  try {
                    const token = await auth.currentUser.getIdToken();
                    const res = await fetch(withTeamSelection(`/api/referrer/applications/${enhancedViewApp.applicationId}/comments`), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ text: referrerNewComment.trim() }),
                    });
                    const data = await res.json();
                    if (!res.ok) { noteTeamSelectionFailure('/api/referrer/applications', res.status, data); throw new Error(data.error || 'Failed to add comment'); }
                    setReferrerNewComment('');
                    setEnhancedComments(data.comments || []);
                  } catch (err: any) {
                    console.error('Failed to add comment:', err.message);
                  }
                },
                handleAddReply: async (commentId: string) => {
                  if (!auth.currentUser || !enhancedViewApp?.applicationId) return;
                  const replyText = referrerReplyInputs[commentId] || '';
                  if (!replyText.trim()) return;
                  try {
                    const token = await auth.currentUser.getIdToken();
                    const res = await fetch(withTeamSelection(`/api/referrer/applications/${enhancedViewApp.applicationId}/comments`), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ text: replyText.trim(), replyToCommentId: commentId }),
                    });
                    const data = await res.json();
                    if (!res.ok) { noteTeamSelectionFailure('/api/referrer/applications', res.status, data); throw new Error(data.error || 'Failed to add reply'); }
                    setReferrerReplyInputs(prev => ({ ...prev, [commentId]: '' }));
                    setReferrerOpenReplyId(null);
                    setEnhancedComments(data.comments || []);
                  } catch (err: any) {
                    console.error('Failed to add reply:', err.message);
                  }
                },
                handleEditComment: async (commentId: string, newText: string) => {
                  if (!auth.currentUser || !enhancedViewApp?.applicationId) return;
                  try {
                    const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/comments/${commentId}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ text: newText.trim() }),
                    });
                    setReferrerEditingCommentId(null);
                    setEnhancedComments(res.comments || []);
                  } catch (err) {
                    console.error('Failed to edit comment:', err);
                  }
                },
                handleEditReply: async (commentId: string, replyId: string, newText: string) => {
                  if (!auth.currentUser || !enhancedViewApp?.applicationId) return;
                  try {
                    const res = await safeFetch(`/api/referrer/applications/${enhancedViewApp.applicationId}/comments/${commentId}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ replyId, replyText: newText.trim() }),
                    });
                    setReferrerEditingReplyId(null);
                    setEnhancedComments(res.comments || []);
                  } catch (err) {
                    console.error('Failed to edit reply:', err);
                  }
                },
                editingCommentId: referrerEditingCommentId,
                setEditingCommentId: setReferrerEditingCommentId,
                editingCommentText: referrerEditingCommentText,
                setEditingCommentText: setReferrerEditingCommentText,
                editingReplyId: referrerEditingReplyId,
                setEditingReplyId: setReferrerEditingReplyId,
                editingReplyText: referrerEditingReplyText,
                setEditingReplyText: setReferrerEditingReplyText,
                openReplyId: referrerOpenReplyId,
                setOpenReplyId: setReferrerOpenReplyId,
                replyInputs: referrerReplyInputs,
                setReplyInputs: setReferrerReplyInputs,
                commentFileName: '',
                commentFileProgress: null,
                setCommentFileName: () => {},
                setCommentFileProgress: () => {},
                handleCommentFileChange: async () => {},

                // ── Admin Notes ──
                adminNoteInput: '',
                setAdminNoteInput: () => {},
                handleUpdateAdminNotes: async () => {},

                // ── Applicant Status ──
                handleUpdateStatus: undefined,
                handleReturnToApplicant: undefined,
                handleToggleApplicationActive: undefined,
                handleUpdateApplicantTags: async () => {},
                selectedApp: enhancedViewApp,
                setSelectedApp: () => {},

                // ── Program Assignment ──
                isEditingProgram,
                setIsEditingProgram,
                editProgram,
                setEditProgram,
                editProgramTerm,
                setEditProgramTerm,
                handleSaveProgram,

                // ── Sponsor/Partner Assignment ──
                handleAssignProvider: async () => {},
                providers: [],
                setProviders: () => {},

                // ── Host Employer / Assignment ──
                isEditingAssignment,
                setIsEditingAssignment,
                editAssignmentEmployer,
                setEditAssignmentEmployer,
                editAssignmentRole,
                setEditAssignmentRole,
                editAssignmentCity,
                setEditAssignmentCity,
                editAssignmentState,
                setEditAssignmentState,
                editAssignmentCountry,
                setEditAssignmentCountry,
                handleSaveAssignment,

                // ── Itinerary Sections ──
                isEditingVisa,
                setIsEditingVisa,
                editVisaNo,
                setEditVisaNo,
                editVisaStatus,
                setEditVisaStatus,
                editVisaDate,
                setEditVisaDate,
                editVisaLocation,
                setEditVisaLocation,
                handleSaveVisaDetails,

                isEditingFlight,
                setIsEditingFlight,
                editFlightAirline,
                setEditFlightAirline,
                editFlightNo,
                setEditFlightNo,
                editFlightDepDateTime,
                setEditFlightDepDateTime,
                editFlightArrDateTime,
                setEditFlightArrDateTime,
                editFlightDepAirport,
                setEditFlightDepAirport,
                editFlightArrAirport,
                setEditFlightArrAirport,
                editFlightNotes,
                setEditFlightNotes,
                handleSaveFlightDetails,

                isEditingEmbassy,
                setIsEditingEmbassy,
                editEmbassyDate,
                setEditEmbassyDate,
                editEmbassyLocation,
                setEditEmbassyLocation,
                editEmbassyResult,
                setEditEmbassyResult,
                handleSaveEmbassyDetails,

                isEditingDs160,
                setIsEditingDs160,
                editDs160No,
                setEditDs160No,
                editDs160QuestionAnswer,
                setEditDs160QuestionAnswer,
                editDs160Status,
                setEditDs160Status,
                handleSaveDs160Details,

                // ── Placement / Master Stage ──
                handleUpdatePlacementStatus: async () => {},
                handleUpdateTalentPoolEnabled: async () => {},

                // ── Document Requests ──
                documentRequests: [],
                newRequestName: '',
                setNewRequestName: () => {},
                newRequestDesc: '',
                setNewRequestDesc: () => {},
                newReqDocumentType: 'Other',
                setNewReqDocumentType: () => {},
                newReqDocPriority: 'normal',
                setNewReqDocPriority: () => {},
                newRequestDueDate: '',
                setNewRequestDueDate: () => {},
                editingRequestId: null,
                setEditingRequestId: () => {},
                editingDocId: null,
                setEditingDocId: () => {},
                editingDocName: '',
                setEditingDocName: () => {},

                // ── Passport/Immigration edit ──
                isEditingPassport,
                setIsEditingPassport,
                editPassportNo,
                setEditPassportNo,
                editPassportCountry,
                setEditPassportCountry,
                editPassportIssue,
                setEditPassportIssue,
                editPassportExpiry,
                setEditPassportExpiry,
                editPassVisaStatus,
                setEditPassVisaStatus,
                editDs2019,
                setEditDs2019,
                editSevisId,
                setEditSevisId,
                editIntendedArrival,
                setEditIntendedArrival,
                editUsDestination,
                setEditUsDestination,
                handleSavePassport,

                // ── Program Preferences edit ──
                editProgStart,
                setEditProgStart,
                editProgDuration,
                setEditProgDuration,
                editProgState,
                setEditProgState,
                editProgIndustry,
                setEditProgIndustry,
                editProgEnglish,
                setEditProgEnglish,
                editProgSkills,
                setEditProgSkills,
                editProgRelocate,
                setEditProgRelocate,
                editProgNotes,
                setEditProgNotes,

                // ── Personal Details edit ──
                handleSavePersonal,
                handleSaveEducation,
                handleSaveWork,
                handleSaveEmergency,

                // ── Auth / misc ──
                getToken: async () => {
                  try { return await auth.currentUser?.getIdToken() || null; } catch { return null; }
                },
                adminEmail: userEmail,
                auditLogs: [],
                handlePhotoUploadReal: async (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!enhancedViewApp?.applicationId) return;
                  const file = e.target.files?.[0];
                  if (!file) return;

                  try {
                    const token = await auth.currentUser?.getIdToken();
                    // Read file as base64
                    const reader = new FileReader();
                    const fileBase64 = await new Promise<string>((resolve, reject) => {
                      reader.onload = () => {
                        const result = reader.result as string;
                        resolve(result.split(',')[1] || result);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                    const res = await fetch(withTeamSelection(`/api/referrer/applications/${enhancedViewApp.applicationId}/update-photo`), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        fileName: file.name,
                        fileBase64,
                        contentType: file.type,
                      }),
                    });
                    const data = await res.json();
                    if (res.ok && data.success) {
                      // Update the selected candidate's profilePhotoUrl state
                      setEnhancedViewApp((prev: any) => prev ? { ...prev, profilePhotoUrl: data.profilePhotoUrl } : prev);
                      // Update candidates list state
                      setCandidates((prev: any[]) => prev.map(c => c.applicationId === enhancedViewApp.applicationId ? { ...c, profilePhotoUrl: data.profilePhotoUrl } : c));
                      notifyToast('success', 'Profile photo updated successfully!');
                    } else {
                      noteTeamSelectionFailure('/api/referrer/applications', res.status, data);
                      throw new Error(data.error || 'Upload failed');
                    }
                  } catch (err: any) {
                    console.error('Referrer profile photo upload failed:', err);
                    notifyToast('error', err.message || 'Failed to upload profile photo. Please try again.');
                  }
                },
                handleUpdateAdminNote: async () => {},
              }}
              events={calendarEvents}
              eventsCtx={{ events: calendarEvents, fetchEvents: fetchReferrerEvents }}
              adminEmail={userEmail}
              isEnhancedReferrer={true}
              currentUserProfile={userProfile}
            />
          </div>
        </div>
      ) : (
        <>
      {activeView === 'inbox' && <ReferrerInboxView candidates={candidates} safeFetch={safeFetch} enhancedPermission={enhancedPermission} onViewApplication={openCandidateFromChat} />}
      {activeView === 'support' && <ReferrerSupportView safeFetch={safeFetch} />}
      {activeView === 'special-requests' && <OrgPortalSpecialRequests />}
      {activeView === 'dashboard' && (
        <>
          {/* §2.1 next-action home, above the shared shell content */}
          <ReferrerHomePanel
            candidates={candidates}
            commissions={commissions}
            invitations={invitations}
            referralCode={effectiveReferralCode}
            referralLink={effectiveReferralLink}
            restricted={restricted}
            commissionsLabel={enhancedPermission ? 'Commissions' : 'My Commissions'}
            onNavigate={(tab) => {
              setView(tab);
              window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab } }));
            }}
            onAddCandidate={() => { if (restricted) { notifyToast('error', REFERRER_RESTRICTED_NOTICE); return; } setView('candidates'); setShowAddCandidate(true); setEmailCheckMsg(''); }}
            onSendInvitation={() => { if (restricted) { notifyToast('error', REFERRER_RESTRICTED_NOTICE); return; } setView('invitations'); }}
          />
          <DashboardShell
            role="referrer"
            userProfile={userProfile}
            onNavigate={(tab) => {
              setView(tab);
              window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab } }));
            }}
            stats={stats}
            candidates={candidates}
            events={calendarEvents}
            loading={loading}
          />
        </>
      )}
      {activeView === 'candidates' && (
        <ReferrerCandidatesView
          candidates={candidates}
          candidateSearch={candidateSearch}
          setCandidateSearch={setCandidateSearch}
          restricted={restricted}
          showAddCandidate={showAddCandidate && !restricted}
          setShowAddCandidate={(v: boolean) => { if (!restricted) setShowAddCandidate(v); }}
          candidateForm={candidateForm}
          setCandidateForm={setCandidateForm}
          referrerPrograms={referrerPrograms}
          programsLoading={programsLoading}
          programsError={programsError}
          onRetryPrograms={() => loadPrograms()}
          addingCandidate={addingCandidate}
          emailCheckMsg={emailCheckMsg}
          setEmailCheckMsg={setEmailCheckMsg}
          slideInCandidate={slideInCandidate}
          setSlideInCandidate={setSlideInCandidate}
          slideInLoading={slideInLoading}
          handleAddCandidate={handleAddCandidate}
          addSuccess={addSuccess}
          onAddAnother={() => { resetCandidateForm(); setAddSuccess(null); setEmailCheckMsg(''); }}
          onAddSuccessDone={() => { setShowAddCandidate(false); setAddSuccess(null); resetCandidateForm(); setEmailCheckMsg(''); }}
          onViewAddedCandidate={(c: any) => { setShowAddCandidate(false); setAddSuccess(null); resetCandidateForm(); setEmailCheckMsg(''); openSlideIn(c); }}
          onDismissAddSuccess={() => { setAddSuccess(null); resetCandidateForm(); }}
          openSlideIn={openSlideIn}
          closeSlideIn={closeSlideIn}
          onFetchCandidates={async () => {
            try {
              const candData = await safeFetch('/api/referrer/candidates');
              setCandidates(candData.candidates || []);
            } catch {}
          }}
          getToken={async () => {
            try { return await auth.currentUser?.getIdToken() || null; } catch { return null; }
          }}
        />
      )}
      {activeView === 'jobs' && JobsView()}
      {activeView === 'commissions' && (!isSuperAgent ? MemberCommissionsView() : enhancedPermission ? CommissionsView() : MyCommissionsView())}
      {activeView === 'referral-links' && ReferralLinksView()}
      {activeView === 'invitations' && InvitationsView()}
      {activeView === 'calendar' && <ReferrerCalendarView auth={auth} />}
      {activeView === 'my-events' && <MyEventsView userRole="referrer" />}
      {activeView === 'performance' && (
        <ReferrerPerformanceView
          candidates={candidates}
          onReferFirstCandidate={() => { if (restricted) { notifyToast('error', REFERRER_RESTRICTED_NOTICE); return; } setView('candidates'); setShowAddCandidate(true); setEmailCheckMsg(''); }}
        />
      )}
      {activeView === 'training' && TrainingView()}
      {activeView === 'marketing' && MarketingView()}
      {activeView === 'profile' && ProfileView()}
      {activeView === 'agents' && enhancedPermission && (
        <OrgTeamManagement apiPrefix="/api/referrer" title="Referrer Team" currentUserId={userId} />
      )}
      </>
      )}
      </div>
    </div>
  );
}
